import { createIcons, Plus, FileText, Download, Sparkles, Filter, Search, Eye, Edit3, Trash2, ShieldAlert, CheckCircle, AlertTriangle, XCircle, RefreshCw, Link as LinkIcon, Power, ArrowRight, Boxes, Cpu, ShieldCheck, GitBranch } from "lucide";
import { contextStore } from "../auth/context-store.js";
import { PermissionCode } from "../../permissions/permissions.js";
import { router } from "../router/router.js";
import type { Equipment, EquipmentType, EquipmentStatus } from "../../equipment/equipment-domain.js";
import { PipingFormBuilder } from "../components/piping-form-builder.js";

export class AppEquipmentListPage extends HTMLElement {
  private escapeHtml(str: any): string {
    if (str === null || str === undefined) return '';
    return String(str).replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  private equipments: Equipment[] = [];
  private areas: any[] = [];
  private projects: any[] = [];
  private unitAreas: any[] = [];
  private clients: any[] = [];
  private isLoading = true;
  private searchTerm = "";
  private clientFilter = "ALL";
  private projectFilter = "ALL";
  private unitAreaFilter = "ALL";
  private typeFilter = "ALL";
  private statusFilter = "ALL";
  private areaFilter = "ALL";
  private unsubscribe: (() => void) | null = null;
  private boundHandleMasterDataUpdated: (e: Event) => void;
  private observedActiveClientId: string | null | undefined;
  private loadGeneration = 0;

  constructor() {
    super();
    this.boundHandleMasterDataUpdated = this.handleMasterDataUpdated.bind(this);
    this.observedActiveClientId = contextStore.get().activeClientId;
    this.clientFilter = this.observedActiveClientId || "ALL";
  }

  private handleMasterDataUpdated(e: Event) {
    void e;
    void this.loadData();
  }

  static get observedAttributes() {
    return ['path'];
  }

  private shouldOpenCreateModal = false;

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (name === 'path' && (newValue === '/equipment/new' || newValue?.includes('/equipment/new')) && oldValue !== newValue) {
      if (!this.isLoading) {
        this.openCreateModal();
      } else {
        this.shouldOpenCreateModal = true;
      }
    }
  }

  connectedCallback() {
    const isNewRoute = window.location.pathname === '/equipment/new' || 
                       window.location.hash.includes('/equipment/new') || 
                       window.location.search.includes('create=true') ||
                       window.location.search.includes('action=new');
    
    if (isNewRoute) {
      this.shouldOpenCreateModal = true;
    }

    void this.loadData();
    this.unsubscribe = contextStore.subscribe(() => {
      void this.loadData();
    });
    window.addEventListener('MASTER_DATA_UPDATED', this.boundHandleMasterDataUpdated);
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
    window.removeEventListener('MASTER_DATA_UPDATED', this.boundHandleMasterDataUpdated);
  }

  private async loadData() {
    const generation = ++this.loadGeneration;
    this.isLoading = true;
    this.render();

    try {
      const context = contextStore.get();
      const activeClientId = context.activeClientId;
      if (activeClientId !== this.observedActiveClientId) {
        this.observedActiveClientId = activeClientId;
        this.clientFilter = activeClientId || "ALL";
        this.projectFilter = "ALL";
        this.unitAreaFilter = "ALL";
      }
      const dummyContext = { activeClientId, permissions: context.permissions, correlationId: '', userId: context.userId, professionalId: context.professionalId || null } as any;
      
      // O filtro de Cliente DEFINE a consulta ao servidor: Cliente escolhido →
      // só os ativos dele (o serviço confere o acesso); "Todos os Clientes" →
      // a união dos Clientes AUTORIZADOS, nunca uma consulta sem WHERE. Antes a
      // busca vinha sempre do Cliente ativo e o filtro só recortava no
      // navegador: escolher outro Cliente mostrava zero linhas.
      const equipmentScope = this.clientFilter === "ALL" ? { allClients: true } : { clientId: this.clientFilter };
      const [eqs, projectsList, unitAreasList, areasList, cls] = await Promise.all([
        window.app.platform.equipmentService.listEquipments(dummyContext, equipmentScope),
        window.app.platform.projectService.listProjects(undefined, undefined, dummyContext).catch(() => []),
        window.app.platform.projectService.listUnitAreas(undefined, undefined, dummyContext).catch(() => []),
        window.app.platform.adminService.listAreas(dummyContext).catch(() => []),
        window.app.platform.adminService.listAuthorizedClients(dummyContext).catch(() => [])
      ]);

      if (generation !== this.loadGeneration) return;
      this.equipments = eqs || [];
      this.projects = projectsList || [];
      this.unitAreas = unitAreasList || [];
      this.areas = areasList || [];
      this.clients = cls || [];
      this.isLoading = false;
      this.render();

      if (this.shouldOpenCreateModal) {
        this.shouldOpenCreateModal = false;
        // Small timeout to ensure DOM is ready after render
        setTimeout(() => this.openCreateModal(), 50);
      }
    } catch (e: any) {
      this.isLoading = false;
      this.renderError(e.message);
    }
  }

  private getFilteredEquipments(): Equipment[] {
    return this.equipments.filter(eq => {
      const matchesClient = this.clientFilter === 'ALL' || eq.clientId === this.clientFilter;
      const matchesProject = this.projectFilter === 'ALL' || eq.projectId === this.projectFilter;
      const matchesUnitArea = this.unitAreaFilter === 'ALL' || eq.unitAreaId === this.unitAreaFilter || eq.areaId === this.unitAreaFilter;
      const matchesSearch = !this.searchTerm || 
        eq.tag.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        eq.name.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        (eq.service && eq.service.toLowerCase().includes(this.searchTerm.toLowerCase())) ||
        (eq.system && eq.system.toLowerCase().includes(this.searchTerm.toLowerCase())) ||
        (eq.manufacturer && eq.manufacturer.toLowerCase().includes(this.searchTerm.toLowerCase()));

      const matchesType = this.typeFilter === 'ALL' || eq.equipmentType === this.typeFilter;
      const matchesStatus = this.statusFilter === 'ALL' || eq.status === this.statusFilter;

      return matchesClient && matchesProject && matchesUnitArea && matchesSearch && matchesType && matchesStatus;
    });
  }

  private getStatusBadge(status: EquipmentStatus): string {
    switch (status) {
      case 'ACTIVE':
        return `<span style="background: rgba(34, 197, 94, 0.15); color: var(--status-success-text); border: 1px solid rgba(34, 197, 94, 0.3); padding: 2px 8px; border-radius: 4px; font-size: 0.75rem; font-weight: 600; display: inline-flex; align-items: center; gap: 4px;"><span style="width: 6px; height: 6px; border-radius: 50%; background: #4ade80;"></span> ATIVO</span>`;
      case 'INACTIVE':
        return `<span style="background: rgba(239, 68, 68, 0.15); color: var(--status-danger-solid); border: 1px solid rgba(239, 68, 68, 0.3); padding: 2px 8px; border-radius: 4px; font-size: 0.75rem; font-weight: 600; display: inline-flex; align-items: center; gap: 4px;"><span style="width: 6px; height: 6px; border-radius: 50%; background: var(--status-danger-solid);"></span> INATIVO</span>`;
      case 'OUT_OF_SERVICE':
        return `<span style="background: var(--status-warning-bg); color: var(--status-warning-text); border: 1px solid var(--status-warning-border); padding: 2px 8px; border-radius: 4px; font-size: 0.75rem; font-weight: 600; display: inline-flex; align-items: center; gap: 4px;"><span style="width: 6px; height: 6px; border-radius: 50%; background: var(--status-warning-text);"></span> FORA DE OPERAÇÃO</span>`;
      default:
        return `<span style="background: rgba(140, 140, 140, 0.15); color: var(--ia-fg-soft, #b5b5b5); padding: 2px 8px; border-radius: 4px; font-size: 0.75rem;">${status}</span>`;
    }
  }

  private getTypeLabel(type: EquipmentType): string {
    const map: Record<string, string> = {
      'PRESSURE_VESSEL': 'Vaso de Pressão (VP)',
      'STORAGE_TANK': 'Tanque de Armazenamento (TQ)',
      'HEAT_EXCHANGER': 'Permutador de Calor (TC)',
      'BOILER': 'Caldeira (CD)',
      'FURNACE': 'Forno Industrial (FN)',
      'PIPING_SYSTEM': 'Sistema de Tubulação (ST)',
      'PIPING_LINE': 'Tubulação',
      'OTHER': 'Outro Equipamento'
    };
    return map[type] || type;
  }

  private renderError(msg: string) {
    this.innerHTML = `
      <div style="padding: 32px; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 8px; margin: 24px; color: var(--status-danger-solid);">
        <h3 style="margin: 0 0 8px 0; font-size: 1.1rem;">Erro ao carregar Equipamentos</h3>
        <p style="margin: 0 0 16px 0; color: var(--color-text-disabled);">${msg}</p>
        <button id="btn-retry" style="background: var(--ia-bg-panel, #27272a); color: var(--color-text-primary); border: 1px solid var(--ia-line, #52525b); padding: 8px 16px; border-radius: 4px; cursor: pointer;">
          Tentar Novamente
        </button>
      </div>
    `;
    this.querySelector('#btn-retry')?.addEventListener('click', () => this.loadData());
  }

  private render() {
    if (this.isLoading) {
      this.innerHTML = `
        <div style="padding: 72px 24px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 14px; color: var(--color-text-muted);">
          <svg class="spinner spinner-dark spinner-lg" viewBox="0 0 24 24" fill="none" aria-label="Carregando...">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-opacity="0.2" stroke-width="3"/>
            <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
          </svg>
          <span class="text-body-small">Carregando cadastro de equipamentos...</span>
        </div>
      `;
      return;
    }

    const filtered = this.getFilteredEquipments();
    const canCreate = contextStore.hasPermission(PermissionCode.EquipmentCreate);
    const canDelete = contextStore.hasPermission(PermissionCode.EquipmentDelete);
    const canUpdate = contextStore.hasPermission(PermissionCode.EquipmentUpdate);
    // [CORREÇÃO PONTUAL] Removido: activeClientName não é mais exibido; filtro local é a referência.

    this.innerHTML = `
      <style>
        .eq-page-container {
          padding: 24px;
          max-width: 1400px;
          margin: 0 auto;
          color: var(--color-text-primary);
        }
        .eq-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          flex-wrap: wrap;
          gap: 16px;
        }
        .eq-title-group h1 {
          font-size: 1.4rem;
          font-weight: 600;
          color: var(--color-text-primary);
          margin: 0 0 4px 0;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .eq-subtitle {
          font-size: 0.85rem;
          color: var(--color-text-primary);
        }
        .eq-toolbar {
          display: flex;
          gap: 12px;
          margin-bottom: 20px;
          flex-wrap: wrap;
          align-items: center;
          background: var(--ia-bg-panel, #18181b);
          padding: 14px;
          border-radius: 8px;
          border: 1px solid var(--ia-line, #27272a);
        }
        .eq-search-box {
          flex: 1;
          min-width: 220px;
          position: relative;
        }
        .eq-search-box input {
          width: 100%;
          background: var(--color-input-bg);
          border: 1px solid var(--color-input-border);
          border-radius: 6px;
          padding: 8px 12px 8px 34px;
          color: var(--color-text-primary);
          font-size: 0.875rem;
          box-sizing: border-box;
        }
        .eq-search-box i {
          position: absolute;
          left: 10px;
          top: 50%;
          transform: translateY(-50%);
          color: var(--color-text-primary);
          pointer-events: none;
        }
        .eq-select {
          background: var(--color-input-bg);
          border: 1px solid var(--color-input-border);
          border-radius: 6px;
          padding: 8px 12px;
          color: var(--color-text-primary);
          font-size: 0.875rem;
          min-width: 150px;
        }
        .btn-primary-eq {
          background: var(--ia-action-bg, var(--color-primary));
          color: var(--ia-action-fg, var(--color-text-primary));
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          font-weight: 500;
          font-size: 0.875rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: background 0.2s;
        }
        .btn-primary-eq:hover {
          background: var(--color-primary);
        }
        .btn-secondary-eq {
          background: var(--ia-bg-panel, #27272a);
          color: var(--color-text-primary);
          border: 1px solid var(--color-input-border);
          padding: 8px 14px;
          border-radius: 6px;
          font-size: 0.875rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: background 0.2s;
        }
        .btn-secondary-eq:hover {
          background: var(--ia-bg-raised, #3f3f46);
        }
        .eq-table-wrapper {
          background: var(--ia-bg-panel, #18181b);
          border: 1px solid var(--ia-line, #27272a);
          border-radius: 8px;
          overflow-x: auto;
        }
        .eq-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.875rem;
          text-align: left;
        }
        .eq-table th {
          background: var(--ia-bg-panel, #27272a);
          color: var(--color-text-disabled);
          padding: 12px 16px;
          font-weight: 600;
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border-bottom: 1px solid var(--ia-line, #3f3f46);
        }
        .eq-table td {
          padding: 14px 16px;
          border-bottom: 1px solid #27272a;
          color: var(--color-text-primary);
        }
        .eq-table tr:hover td {
          background: rgba(255, 255, 255, 0.02);
        }
        .eq-tag-cell {
          font-weight: 600;
          color: var(--ia-info-fg, #60a5fa);
          font-family: monospace;
          font-size: 0.95rem;
        }
        .eq-action-btn {
          background: var(--ia-bg-panel, #27272a);
          border: 1px solid var(--color-input-border);
          color: var(--color-text-primary);
          padding: 5px 10px;
          border-radius: 4px;
          font-size: 0.75rem;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          transition: background 0.15s, border-color 0.15s;
          margin-right: 4px;
        }
        .eq-action-btn:hover {
          background: var(--ia-bg-raised, #3f3f46);
          color: var(--color-text-primary);
        }
        .eq-action-btn.danger:hover {
          background: var(--status-danger-solid);
          border-color: var(--status-danger-solid);
          color: var(--color-text-primary);
        }
        .eq-action-btn.warning:hover {
          background: var(--status-warning-text);
          border-color: var(--status-warning-text);
          color: var(--color-text-primary);
        }
        .eq-stat-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 12px;
          margin-bottom: 20px;
        }
        .eq-stat-card {
          background: var(--ia-bg-panel, #18181b);
          border: 1px solid var(--ia-line, #27272a);
          border-radius: 8px;
          padding: 14px 18px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .eq-stat-num {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--color-text-primary);
        }
        .eq-stat-label {
          font-size: 0.75rem;
          color: var(--color-text-primary);
          text-transform: uppercase;
        }
      </style>

      <div class="eq-page-container">
        <!-- HEADER -->
        <div class="eq-header">
          <div class="eq-title-group">
            <h1><i data-lucide="boxes" style="color: var(--ia-info-fg, #60a5fa); width: 24px; height: 24px;"></i> Cadastro de Equipamento / Tubulação</h1>
            <!-- [CORREÇÃO PONTUAL] Removido texto residual "Cliente Ativo". O filtro local é a única referência visual de Cliente. -->
            <div class="eq-subtitle">Gestão de Equipamentos Estáticos, Tubulações e Histórico Operacional</div>
          </div>
          <div style="display: flex; gap: 8px;">
            <button class="btn-secondary-eq" id="btn-refresh-list">
              <i data-lucide="refresh-cw" style="width: 16px; height: 16px;"></i> Atualizar
            </button>
            <button class="btn-secondary-eq" id="btn-goto-hub">
              <i data-lucide="link" style="width: 16px; height: 16px;"></i> Hub Técnico
            </button>
            ${canCreate ? `
              <button class="btn-primary-eq" id="btn-new-eq">
                <i data-lucide="plus" style="width: 18px; height: 18px;"></i> Novo Equipamento / Tubulação
              </button>
            ` : ''}
          </div>
        </div>

        <!-- STATS -->
        <div class="eq-stat-cards">
          <div class="eq-stat-card">
            <div>
              <div class="eq-stat-label">Total Cadastrado</div>
              <div class="eq-stat-num">${this.equipments.length}</div>
            </div>
            <i data-lucide="boxes" style="width: 28px; height: 28px; color: var(--ia-info-fg, #60a5fa); ;"></i>
          </div>
          <div class="eq-stat-card">
            <div>
              <div class="eq-stat-label">Ativos em Operação</div>
              <div class="eq-stat-num" style="color: var(--status-success-text);">${this.equipments.filter(e => e.status === 'ACTIVE').length}</div>
            </div>
            <i data-lucide="check-circle" style="width: 28px; height: 28px; color: var(--status-success-text); ;"></i>
          </div>
          <div class="eq-stat-card">
            <div>
              <div class="eq-stat-label">Fora de Serviço</div>
              <div class="eq-stat-num" style="color: var(--status-warning-text);">${this.equipments.filter(e => e.status === 'OUT_OF_SERVICE').length}</div>
            </div>
            <i data-lucide="alert-triangle" style="width: 28px; height: 28px; color: var(--status-warning-text); ;"></i>
          </div>
          <div class="eq-stat-card">
            <div>
              <div class="eq-stat-label">Inativados</div>
              <div class="eq-stat-num" style="color: var(--status-danger-solid);">${this.equipments.filter(e => e.status === 'INACTIVE').length}</div>
            </div>
            <i data-lucide="x-circle" style="width: 28px; height: 28px; color: var(--status-danger-solid); ;"></i>
          </div>
        </div>

        <!-- TOOLBAR & FILTERS -->
        <div class="eq-toolbar">
          <div class="eq-search-box">
            <i data-lucide="search" style="width: 16px; height: 16px;"></i>
            <input type="text" id="eq-search-input" placeholder="Buscar por TAG, nome, sistema, serviço, fabricante..." value="${this.searchTerm}">
          </div>

          <select class="eq-select" id="eq-client-filter" style="max-width: 200px;">
            <option value="ALL" ${this.clientFilter === 'ALL' ? 'selected' : ''}>Todos os Clientes</option>
            ${this.clients.map(c => `<option value="${c.id}" ${this.clientFilter === c.id ? 'selected' : ''}>${c.name || c.legalName}</option>`).join('')}
          </select>

          <select class="eq-select" id="eq-project-filter" style="max-width: 200px;" ${this.clientFilter === 'ALL' ? 'disabled style=";"' : ''}>
            <option value="ALL" ${this.projectFilter === 'ALL' ? 'selected' : ''}>${this.clientFilter === 'ALL' ? 'Projetos (Selecione Cliente)' : 'Todos os Projetos'}</option>
            ${(this.clientFilter === 'ALL' ? this.projects : this.projects.filter(p => p.clientId === this.clientFilter)).map(p => `<option value="${p.id}" ${this.projectFilter === p.id ? 'selected' : ''}>${p.code} - ${p.name}</option>`).join('')}
          </select>

          <select class="eq-select" id="eq-unit-filter" style="max-width: 200px;" ${this.projectFilter === 'ALL' ? 'disabled style=";"' : ''}>
            <option value="ALL" ${this.unitAreaFilter === 'ALL' ? 'selected' : ''}>${this.projectFilter === 'ALL' ? 'Unid/Áreas (Selecione Projeto)' : 'Todas as Unidades/Áreas'}</option>
            ${(this.projectFilter === 'ALL' ? this.unitAreas : this.unitAreas.filter(u => u.projectId === this.projectFilter)).map(u => `<option value="${u.id}" ${this.unitAreaFilter === u.id ? 'selected' : ''}>${u.code} - ${u.name}</option>`).join('')}
          </select>

          <select class="eq-select" id="eq-type-filter">
            <option value="ALL" ${this.typeFilter === 'ALL' ? 'selected' : ''}>Todos os Tipos</option>
            <option value="PRESSURE_VESSEL" ${this.typeFilter === 'PRESSURE_VESSEL' ? 'selected' : ''}>Vaso de Pressão (VP)</option>
            <option value="STORAGE_TANK" ${this.typeFilter === 'STORAGE_TANK' ? 'selected' : ''}>Tanque de Armazenamento (TQ)</option>
            <option value="HEAT_EXCHANGER" ${this.typeFilter === 'HEAT_EXCHANGER' ? 'selected' : ''}>Permutador de Calor (TC)</option>
            <option value="BOILER" ${this.typeFilter === 'BOILER' ? 'selected' : ''}>Caldeira (CD)</option>
            <option value="FURNACE" ${this.typeFilter === 'FURNACE' ? 'selected' : ''}>Forno Industrial (FN)</option>
            <option value="PIPING_SYSTEM" ${this.typeFilter === 'PIPING_SYSTEM' ? 'selected' : ''}>Sistema de Tubulação (ST)</option>
            <option value="PIPING_LINE" ${this.typeFilter === 'PIPING_LINE' ? 'selected' : ''}>Tubulação</option>
            <option value="OTHER" ${this.typeFilter === 'OTHER' ? 'selected' : ''}>Outros</option>
          </select>

          <select class="eq-select" id="eq-status-filter">
            <option value="ALL" ${this.statusFilter === 'ALL' ? 'selected' : ''}>Todos os Status</option>
            <option value="ACTIVE" ${this.statusFilter === 'ACTIVE' ? 'selected' : ''}>Ativo</option>
            <option value="OUT_OF_SERVICE" ${this.statusFilter === 'OUT_OF_SERVICE' ? 'selected' : ''}>Fora de Serviço</option>
            <option value="INACTIVE" ${this.statusFilter === 'INACTIVE' ? 'selected' : ''}>Inativo</option>
          </select>
        </div>

        <!-- TABLE -->
        <div class="eq-table-wrapper">
          <table class="eq-table">
            <thead>
              <tr>
                <th style="width: 130px;">TAG</th>
                <th>Nome / Descrição</th>
                <th style="min-width: 220px;">Hierarquia Cadastral</th>
                <th>Tipo de Ativo</th>
                <th>Serviço / Fluido</th>
                <th style="width: 110px;">Status</th>
                <th style="width: 220px; text-align: right;">Ações</th>
              </tr>
            </thead>
            <tbody>
              ${filtered.length === 0 ? `
                <tr>
                  <td colspan="7" style="padding: 48px 16px; text-align: center; color: var(--color-text-primary);">
                    <i data-lucide="boxes" style="width: 40px; height: 40px; margin-bottom: 12px; ;"></i>
                    <p style="margin: 0; font-size: 0.95rem;">Nenhum equipamento cadastrado.</p>
                    <p style="margin: 4px 0 0 0; font-size: 0.8rem; color: var(--color-text-primary);">Cadastre novos equipamentos ou ajuste os filtros da pesquisa.</p>
                  </td>
                </tr>
              ` : filtered.map(eq => {
                const eqAny = eq as any;
                const client = this.clients.find(c => c.id === eq.clientId);
                const clientName = client ? (client.name || client.legalName) : (eqAny.clientName || '-');
                const project = this.projects.find(p => p.id === eq.projectId);
                const projectName = project ? `${project.code} - ${project.name}` : (eqAny.projectName || 'Pendente de Vínculo');
                const unit = this.unitAreas.find(u => u.id === (eq.unitAreaId || eq.areaId)) || this.areas.find(a => a.id === (eq.unitAreaId || eq.areaId));
                const unitName = unit ? `${unit.code ? unit.code + ' - ' : ''}${unit.name}` : (eqAny.unitAreaName || eq.location || 'Pendente de Vínculo');
                return `
                  <tr>
                    <td class="eq-tag-cell">${eq.tag}</td>
                    <td>
                      <div style="font-weight: 500; color: var(--color-text-primary);">${eq.name}</div>
                      ${eq.equipmentType === 'PIPING_LINE' ? `
                        <div style="margin-top: 4px;">
                          <span style="background: rgba(59, 130, 246, 0.15); color: var(--ia-info-fg, #60a5fa); border: 1px solid rgba(59, 130, 246, 0.3); padding: 2px 7px; border-radius: 4px; font-size: 0.72rem; font-weight: 500; display: inline-flex; align-items: center; gap: 4px;">
                            <i data-lucide="git-branch" style="width: 12px; height: 12px;"></i> Sistema: <strong>${eq.system || 'Vinculado'}</strong>
                          </span>
                        </div>
                      ` : ''}
                      ${eq.manufacturer ? `<div style="font-size: 0.75rem; color: var(--color-text-primary); margin-top: 2px;">Fabr: ${eq.manufacturer} ${eq.model ? `(${eq.model})` : ''}</div>` : ''}
                    </td>
                    <td>
                      <div style="font-size: 0.75rem; display: flex; flex-direction: column; gap: 3px;">
                        <div style="color: var(--ia-info-fg, #93c5fa); font-weight: 600; white-space: nowrap;">
                          <i data-lucide="building-2" style="width: 1em; height: 1em; display: inline-block; vertical-align: middle; margin-top: -2px; margin-right: 4px;"></i> ${this.escapeHtml(clientName)}
                        </div>
                        <div style="color: var(--color-text-muted); font-weight: 500;">
                          <i data-lucide="folder" style="width: 1em; height: 1em; display: inline-block; vertical-align: middle; margin-top: -2px; margin-right: 4px;"></i> ${this.escapeHtml(projectName)}
                        </div>
                        <div style="color: var(--color-graphite-300);">
                          <i data-lucide="folder" style="width: 1em; height: 1em; display: inline-block; vertical-align: middle; margin-top: -2px; margin-right: 4px;"></i> ${this.escapeHtml(unitName)}
                        </div>
                      </div>
                    </td>
                    <td><span style="font-size: 0.82rem; color: var(--color-text-disabled);">${this.getTypeLabel(eq.equipmentType)}</span></td>
                    <td><span style="font-size: 0.82rem; color: var(--color-text-disabled);">${eq.service || '-'}</span></td>
                    <td>${this.getStatusBadge(eq.status)}</td>
                    <td style="text-align: right;">
                      <button class="eq-action-btn btn-view-eq" data-id="${eq.id}" title="Ver Detalhes e Abas Técnicas">
                        <i data-lucide="eye" style="width: 14px; height: 14px;"></i> Detalhes
                      </button>
                      ${canUpdate ? `
                        <button class="eq-action-btn btn-edit-eq" data-id="${eq.id}" title="Editar Equipamento">
                          <i data-lucide="edit-3" style="width: 14px; height: 14px;"></i> Editar
                        </button>
                      ` : ''}
                      ${eq.status === 'ACTIVE' ? `
                        <button class="eq-action-btn warning btn-inactivate-eq" data-id="${eq.id}" data-tag="${this.escapeHtml(eq.tag)}" title="Inativar Equipamento">
                          <i data-lucide="power" style="width: 14px; height: 14px;"></i> Inativar
                        </button>
                      ` : `
                        <button class="eq-action-btn btn-reactivate-eq" data-id="${eq.id}" data-tag="${this.escapeHtml(eq.tag)}" title="Reativar Equipamento" style="color: var(--status-success-text);">
                          <i data-lucide="refresh-cw" style="width: 14px; height: 14px;"></i> Reativar
                        </button>
                      `}
                      ${canDelete ? `
                        <button class="eq-action-btn danger btn-delete-eq" data-id="${eq.id}" data-tag="${this.escapeHtml(eq.tag)}" title="Excluir Definitivamente">
                          <i data-lucide="trash-2" style="width: 14px; height: 14px;"></i>
                        </button>
                      ` : ''}
                    </td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
        </div>
      </div>

      <!-- MODAL GENÉRICO PARA CRIAÇÃO / EDIÇÃO / INATIVAÇÃO / EXCLUSÃO -->
      <div id="eq-modal-container"></div>
    `;

    createIcons({
      icons: { Plus, FileText, Download, Sparkles, Filter, Search, Eye, Edit3, Trash2, ShieldAlert, CheckCircle, AlertTriangle, XCircle, RefreshCw, LinkIcon, Power, ArrowRight, Boxes, Cpu, ShieldCheck, GitBranch },
      nameAttr: "data-lucide",
      root: this
    });

    this.bindEvents();
  }

  private bindEvents() {
    // Toolbar search & filters
    const searchInput = this.querySelector('#eq-search-input') as HTMLInputElement;
    searchInput?.addEventListener('input', (e) => {
      this.searchTerm = (e.target as HTMLInputElement).value;
      this.render();
    });

    this.querySelector('#eq-client-filter')?.addEventListener('change', (e) => {
      this.clientFilter = (e.target as HTMLSelectElement).value;
      this.projectFilter = "ALL";
      this.unitAreaFilter = "ALL";
      void this.loadData();
    });

    this.querySelector('#eq-project-filter')?.addEventListener('change', (e) => {
      this.projectFilter = (e.target as HTMLSelectElement).value;
      this.unitAreaFilter = "ALL";
      this.render();
    });

    this.querySelector('#eq-unit-filter')?.addEventListener('change', (e) => {
      this.unitAreaFilter = (e.target as HTMLSelectElement).value;
      this.render();
    });

    this.querySelector('#eq-type-filter')?.addEventListener('change', (e) => {
      this.typeFilter = (e.target as HTMLSelectElement).value;
      this.render();
    });

    this.querySelector('#eq-status-filter')?.addEventListener('change', (e) => {
      this.statusFilter = (e.target as HTMLSelectElement).value;
      this.render();
    });

    this.querySelector('#btn-refresh-list')?.addEventListener('click', () => {
      this.loadData();
    });

    this.querySelector('#btn-goto-hub')?.addEventListener('click', () => {
      router.navigate('/technical-assets');
    });

    this.querySelector('#btn-new-eq')?.addEventListener('click', () => {
      this.openCreateModal();
    });

    // Action buttons in table
    this.querySelectorAll('.btn-view-eq').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        if (id) router.navigate(`/equipment/${id}`);
      });
    });

    this.querySelectorAll('.btn-edit-eq').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        if (id) {
          const eq = this.equipments.find(item => item.id === id);
          if (eq) this.openEditModal(eq);
        }
      });
    });

    this.querySelectorAll('.btn-inactivate-eq').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        const tag = (e.currentTarget as HTMLElement).getAttribute('data-tag');
        if (id && tag) this.openInactivateModal(id, tag);
      });
    });

    this.querySelectorAll('.btn-reactivate-eq').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        const tag = (e.currentTarget as HTMLElement).getAttribute('data-tag');
        if (id && confirm(`Deseja reativar o equipamento ${tag}?`)) {
          try {
            const dummyContext = { activeClientId: contextStore.get().activeClientId, permissions: contextStore.get().permissions, correlationId: '', userId: contextStore.get().userId } as any;
            await window.app.platform.equipmentService.reactivateEquipment(dummyContext, id, "Reativação solicitada pelo usuário");
            this.loadData();
          } catch (err: any) {
            alert(`Erro ao reativar: ${err.message}`);
          }
        }
      });
    });

    this.querySelectorAll('.btn-delete-eq').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        const tag = (e.currentTarget as HTMLElement).getAttribute('data-tag');
        if (id && tag) this.openDeleteModal(id, tag);
      });
    });
  }

  // CREATE MODAL
  private async openCreateModal() {
    const modalContainer = this.querySelector('#eq-modal-container') as HTMLElement;
    if (!modalContainer) return;

    const pipingEngineeringHtml = await PipingFormBuilder.renderPipingFields({}, this.equipments);

    modalContainer.innerHTML = `
      <div class="modal-backdrop" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.8); z-index: 999; display: flex; align-items: center; justify-content: center; padding: 20px;">
        <div style="background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 8px; width: 100%; max-width: 820px; max-height: 92vh; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.5);">
          <div style="padding: 16px 20px; border-bottom: 1px solid var(--ia-line, #27272a); display: flex; justify-content: space-between; align-items: center; background: var(--ia-bg-panel, #202024);">
            <h3 style="margin: 0; font-size: 1.15rem; color: var(--color-text-primary); display: flex; align-items: center; gap: 8px;">
              <i data-lucide="plus" style="color: var(--ia-info-fg, #60a5fa); width: 20px; height: 20px;"></i> Cadastrar Novo Equipamento / Tubulação
            </h3>
            <button id="btn-close-modal" style="background: transparent; border: none; color: var(--color-text-disabled); font-size: 1.2rem; cursor: pointer;">✕</button>
          </div>
          
          <form id="form-create-eq" style="padding: 20px; overflow-y: auto; display: flex; flex-direction: column; gap: 16px;">
            <div id="create-eq-error-banner" style="display: none; background: rgba(239, 68, 68, 0.15); border: 1px solid var(--status-danger-solid); color: var(--status-danger-border); padding: 10px 14px; border-radius: 6px; font-size: 0.85rem; align-items: center; gap: 8px;">
              <i data-lucide="alert-triangle" style="width: 16px; height: 16px; flex-shrink: 0;"></i>
              <span id="create-eq-error-text"></span>
            </div>

            <!-- SEÇÃO 1: IDENTIFICAÇÃO E HIERARQUIA CADASTRAL OBRIGATÓRIA -->
            <div style="background: var(--ia-bg-panel, #121215); border: 1px solid var(--ia-line, #27272a); border-radius: 6px; padding: 14px;">
              <div style="font-size: 0.85rem; font-weight: 600; color: var(--ia-info-fg, #60a5fa); margin-bottom: 12px; display: flex; align-items: center; gap: 6px;">
                <i data-lucide="boxes" style="width: 15px; height: 15px;"></i> 1. Identificação Geral & Hierarquia Organizacional
              </div>

              <!-- HIERARQUIA OBRIGATÓRIA: CLIENTE -> PROJETO -> UNIDADE/ÁREA -->
              <div style="background: rgba(37, 99, 235, 0.08); border: 1px solid rgba(59, 130, 246, 0.25); border-radius: 6px; padding: 12px; margin-bottom: 14px;">
                <div style="font-size: 0.78rem; font-weight: 700; color: var(--color-border-default); margin-bottom: 10px; text-transform: uppercase; letter-spacing: 0.05em; display: flex; align-items: center; gap: 6px;">
                  <i data-lucide="git-branch" style="width: 14px; height: 14px;"></i> Hierarquia Obrigatória: Cliente &rarr; Projeto &rarr; Unidade/Área
                </div>

                <div style="margin-bottom: 10px;">
                  <label style="display: block; font-size: 0.8rem; color: var(--color-graphite-300); margin-bottom: 4px; font-weight: 600;">1. Cliente *</label>
                  <select name="clientId" id="create-eq-client-select" required style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                    <option value="">Selecione o Cliente...</option>
                    ${this.clients.map(c => `<option value="${c.id}">${c.name || c.legalName}</option>`).join('')}
                  </select>
                </div>

                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
                  <div>
                    <label style="display: block; font-size: 0.8rem; color: var(--color-graphite-300); margin-bottom: 4px; font-weight: 600;">2. Projeto Vinculado *</label>
                    <select name="projectId" id="create-eq-project-select" required disabled style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary); ;">
                      <option value="">Selecione o Cliente primeiro...</option>
                    </select>
                  </div>
                  <div>
                    <label style="display: block; font-size: 0.8rem; color: var(--color-graphite-300); margin-bottom: 4px; font-weight: 600;">3. Unidade / Área do Projeto *</label>
                    <select name="unitAreaId" id="create-eq-unitarea-select" required disabled style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary); ;">
                      <option value="">Selecione o Projeto primeiro...</option>
                    </select>
                  </div>
                </div>

                <div id="create-eq-hierarchy-alert" style="display: none; margin-top: 10px; background: rgba(245, 158, 11, 0.15); border: 1px solid rgba(245, 158, 11, 0.4); color: var(--status-warning-text); padding: 10px 12px; border-radius: 6px; font-size: 0.8rem; line-height: 1.4;">
                </div>
              </div>

              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
                <div>
                  <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">TAG do Ativo *</label>
                  <input type="text" name="tag" required placeholder="Ex: V-101, TQ-201, TB-301" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary); font-family: monospace; font-weight: 600;">
                </div>
                <div>
                  <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Tipo de Ativo *</label>
                  <select name="equipmentType" id="create-eq-type-select" required style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                    <option value="PRESSURE_VESSEL">Vaso de Pressão (VP)</option>
                    <option value="STORAGE_TANK">Tanque de Armazenamento (TQ)</option>
                    <option value="HEAT_EXCHANGER">Permutador de Calor (TC)</option>
                    <option value="BOILER">Caldeira (CD)</option>
                    <option value="FURNACE">Forno Industrial (FN)</option>
                    <option value="PIPING_SYSTEM">Sistema de Tubulação (ST)</option>
                    <option value="PIPING_LINE">Tubulação</option>
                    <option value="OTHER">Outro</option>
                  </select>
                </div>
              </div>

              <div style="margin-top: 10px;">
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Nome / Descrição Resumida *</label>
                <input type="text" name="name" required placeholder="Ex: Linha de Recalque de Amônia 4 pol" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>

              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-top: 10px;">
                <div>
                  <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Fabricante</label>
                  <input type="text" name="manufacturer" placeholder="Ex: Confab, Jaraguá" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                </div>
                <div>
                  <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Ano Fabricação</label>
                  <input type="number" name="yearOfManufacture" placeholder="2020" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                </div>
              </div>
            </div>

            <!-- SEÇÃO ESPECÍFICA DE TUBULAÇÃO (PIPING_LINE) -->
            <div id="create-piping-section" style="display: none; background: rgba(59, 130, 246, 0.05); border: 1px solid rgba(59, 130, 246, 0.25); border-radius: 6px; padding: 14px;">
              <div style="font-size: 0.85rem; font-weight: 600; color: var(--color-border-default); margin-bottom: 12px; display: flex; align-items: center; gap: 6px;">
                <i data-lucide="git-branch" style="width: 15px; height: 15px;"></i> Especificidades de Tubulação & DE / PARA
              </div>
              
              <div style="margin-bottom: 12px;">
                <label style="display: block; font-size: 0.8rem; color: var(--color-border-default); font-weight: 600; margin-bottom: 4px;">
                  Sistema de Tubulação (ST) Vinculado *
                </label>
                <select name="pipingSystemRef" id="create-piping-system-ref" disabled style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-ink); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary); ;">
                  <option value="">Selecione o Cliente primeiro...</option>
                </select>
                <div id="no-piping-systems-msg" style="display: none; margin-top: 6px; background: rgba(239, 68, 68, 0.15); border: 1px solid rgba(239, 68, 68, 0.3); border-radius: 6px; padding: 8px; color: var(--status-danger-border); font-size: 0.8rem;">
                  Nenhum Sistema de Tubulação (ST) cadastrado para este cliente. Cadastre um ST antes de vincular tubulações.
                </div>
              </div>

              ${pipingEngineeringHtml}
            </div>

            <!-- SEÇÃO 2: PROJETO VS OPERAÇÃO -->
            <div style="background: var(--ia-bg-panel, #121215); border: 1px solid var(--ia-line, #27272a); border-radius: 6px; padding: 14px;">
              <div style="font-size: 0.85rem; font-weight: 600; color: var(--color-text-muted); margin-bottom: 12px; display: flex; align-items: center; gap: 6px;">
                <i data-lucide="cpu" style="width: 15px; height: 15px;"></i> 2. Parâmetros de Projeto vs Operação (Estritamente Separados)
              </div>

              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 14px;">
                <!-- PROJETO -->
                <div style="background: var(--color-input-bg); border: 1px solid #27272a; border-radius: 6px; padding: 12px;">
                  <div style="font-size: 0.8rem; font-weight: 700; color: var(--ia-info-fg, #60a5fa); margin-bottom: 8px;">Dados de Projeto</div>
                  <div style="display: flex; gap: 6px; margin-bottom: 8px;">
                    <div style="flex: 2;">
                      <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Pressão de Projeto</label>
                      <input type="number" step="0.01" name="designPressure" placeholder="Ex: 15.5" style="width: 100%; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 8px; color: var(--color-text-primary);">
                    </div>
                    <div style="flex: 1;">
                      <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Unidade</label>
                      <select name="designPressureUnit" style="width: 100%; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 8px; color: var(--color-text-primary);">
                        <option value="kgf/cm²">kgf/cm²</option>
                        <option value="bar">bar</option>
                        <option value="psi">psi</option>
                        <option value="MPa">MPa</option>
                      </select>
                    </div>
                  </div>
                  <div style="display: flex; gap: 6px; margin-bottom: 8px;">
                    <div style="flex: 2;">
                      <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Temp. de Projeto</label>
                      <input type="number" step="0.1" name="designTemperature" placeholder="Ex: 120" style="width: 100%; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 8px; color: var(--color-text-primary);">
                    </div>
                    <div style="flex: 1;">
                      <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Unidade</label>
                      <select name="designTemperatureUnit" style="width: 100%; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 8px; color: var(--color-text-primary);">
                        <option value="°C">°C</option>
                        <option value="°F">°F</option>
                        <option value="K">K</option>
                      </select>
                    </div>
                  </div>
                  <div style="display: flex; gap: 6px; margin-bottom: 8px;">
                    <div style="flex: 2;">
                      <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">PMTA</label>
                      <input type="number" step="0.1" name="pmtaValue" placeholder="Ex: 10" style="width: 100%; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 8px; color: var(--color-text-primary);">
                    </div>
                    <div style="flex: 1;">
                      <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Unidade</label>
                      <select name="pmtaUnit" style="width: 100%; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 8px; color: var(--color-text-primary);">
                        <option value="kgf/cm²">kgf/cm²</option>
                        <option value="bar">bar</option>
                        <option value="MPa">MPa</option>
                        <option value="psi">psi</option>
                      </select>
                    </div>
                  </div>
                  <div id="create-material-container">
                    <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Material de Construção</label>
                    <input type="text" name="material" placeholder="Ex: ASTM A-106 Gr. B, ASTM A-516 Gr. 70" style="width: 100%; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 8px; color: var(--color-text-primary);">
                  </div>
                  <div style="margin-top: 8px; display: grid; grid-template-columns: 1fr 1fr; gap: 8px;">
                    <div>
                      <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Desenho de Referência</label>
                      <input type="text" name="drawingReference" placeholder="Ex: DWG-3201-001" style="width: 100%; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 8px; color: var(--color-text-primary);">
                    </div>
                    <div>
                      <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Fluxograma (P&ID/PFD)</label>
                      <input type="text" name="flowDiagramReference" placeholder="Ex: P&ID-3201-001" style="width: 100%; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 8px; color: var(--color-text-primary);">
                    </div>
                  </div>
                </div>

                <!-- OPERAÇÃO -->
                <div style="background: var(--color-input-bg); border: 1px solid #27272a; border-radius: 6px; padding: 12px;">
                  <div style="font-size: 0.8rem; font-weight: 700; color: var(--status-success-text); margin-bottom: 8px;">Dados de Operação</div>
                  <div style="display: flex; gap: 6px; margin-bottom: 8px;">
                    <div style="flex: 2;">
                      <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Pressão de Operação</label>
                      <input type="number" step="0.01" name="operatingPressure" placeholder="Ex: 12.0" style="width: 100%; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 8px; color: var(--color-text-primary);">
                    </div>
                    <div style="flex: 1;">
                      <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Unidade</label>
                      <select name="operatingPressureUnit" style="width: 100%; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 8px; color: var(--color-text-primary);">
                        <option value="kgf/cm²">kgf/cm²</option>
                        <option value="bar">bar</option>
                        <option value="psi">psi</option>
                        <option value="MPa">MPa</option>
                      </select>
                    </div>
                  </div>
                  <div style="display: flex; gap: 6px; margin-bottom: 8px;">
                    <div style="flex: 2;">
                      <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Temp. de Operação</label>
                      <input type="number" step="0.1" name="operatingTemperature" placeholder="Ex: 95" style="width: 100%; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 8px; color: var(--color-text-primary);">
                    </div>
                    <div style="flex: 1;">
                      <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Unidade</label>
                      <select name="operatingTemperatureUnit" style="width: 100%; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 8px; color: var(--color-text-primary);">
                        <option value="°C">°C</option>
                        <option value="°F">°F</option>
                        <option value="K">K</option>
                      </select>
                    </div>
                  </div>
                  <div style="display: grid; grid-template-columns: 2fr 1fr; gap: 6px;">
                    <div>
                      <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Fluido de Processo</label>
                      <input type="text" name="fluid" placeholder="Ex: Amônia Anidra, Vapor Saturado" style="width: 100%; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 8px; color: var(--color-text-primary);">
                    </div>
                    <div>
                      <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Classe Fluido</label>
                      <select name="fluidClass" style="width: 100%; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 8px; color: var(--color-text-primary);">
                        <option value="">Selecione</option>
                        <option value="CLASSE_A">Classe A (Tóxico/Inflamável)</option>
                        <option value="CLASSE_B">Classe B (Combustível)</option>
                        <option value="CLASSE_C">Classe C (Vapor/Água)</option>
                        <option value="CLASSE_D">Classe D (Outros)</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- SEÇÃO 3: INTEGRIDADE, ESPESSURAS E NORMAS -->
            <div style="background: var(--ia-bg-panel, #121215); border: 1px solid var(--ia-line, #27272a); border-radius: 6px; padding: 14px;">
              <div style="font-size: 0.85rem; font-weight: 600; color: var(--status-warning-text); margin-bottom: 12px; display: flex; align-items: center; gap: 6px;">
                <i data-lucide="shield-check" style="width: 15px; height: 15px;"></i> 3. Espessuras, Normas e Enquadramento NR-13
              </div>

              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 12px;">
                <div class="generic-thickness-field" style="position: relative;">
                  <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Espessura Nominal (mm)</label>
                  <input type="number" step="0.01" name="nominalThickness" id="create-nominal-thickness" placeholder="Ex: 6.02" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                  <div id="create-nominal-thickness-helper" style="display: none; font-size: 0.7rem; color: var(--color-text-disabled); margin-top: 4px;">Definida pelo Cadastro Técnico Assistido de Tubulação</div>
                </div>
                <div>
                  <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Sobre-espessura de Corrosão (mm)</label>
                  <input type="number" step="0.01" name="corrosionAllowance" placeholder="Ex: 1.50" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                </div>
              </div>

              <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; margin-bottom: 12px;">
                <div>
                  <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Norma de Fabricação / Projeto</label>
                  <input type="text" name="fabricationStandard" id="create-fabrication-standard" placeholder="Ex: ASME VIII Div 1" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                </div>
                <div class="generic-projectcode-field" style="position: relative;">
                  <label style="display: flex; justify-content: space-between; align-items: center; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Código de Projeto</label>
                  <input type="text" name="projectCode" id="create-project-code" placeholder="Ex: ASME B31.3" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                  <div id="create-project-code-helper" style="display: none; font-size: 0.7rem; color: var(--color-text-disabled); margin-top: 4px;">Definido pelo Cadastro Técnico Assistido de Tubulação</div>
                </div>
                <div>
                  <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Código de Inspeção em Serviço</label>
                  <input type="text" name="inServiceInspectionCode" placeholder="Ex: API 510, NR-13" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                </div>
              </div>

              <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; background: var(--color-input-bg); border: 1px solid #27272a; padding: 10px; border-radius: 6px;">
                <div>
                  <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Aplicabilidade NR-13</label>
                  <select name="nr13Applicability" style="width: 100%; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                    <option value="APPLICABLE">Aplicável</option>
                    <option value="NOT_APPLICABLE">Não Aplicável</option>
                    <option value="UNCLASSIFIED" selected>Não Classificado</option>
                  </select>
                </div>
                <div>
                  <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Categoria NR-13</label>
                  <select name="nr13Category" style="width: 100%; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                    <option value="">Nenhuma / Sem Cat.</option>
                    <option value="I">Categoria I</option>
                    <option value="II">Categoria II</option>
                    <option value="III">Categoria III</option>
                    <option value="IV">Categoria IV</option>
                    <option value="V">Categoria V</option>
                  </select>
                </div>
                <div>
                  <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Criticidade Geral</label>
                  <select name="criticality" style="width: 100%; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                    <option value="MEDIUM">Média</option>
                    <option value="LOW">Baixa</option>
                    <option value="HIGH">Alta</option>
                    <option value="CRITICAL">Crítica</option>
                  </select>
                </div>
              </div>
            </div>

            <!-- SEÇÃO 4: PROTEÇÃO / REVESTIMENTOS -->
            <div style="background: var(--ia-bg-panel, #121215); border: 1px solid var(--ia-line, #27272a); border-radius: 6px; padding: 14px; margin-bottom: 14px;">
              <div style="font-size: 0.85rem; font-weight: 600; color: var(--ia-info-fg, #a78bfa); margin-bottom: 12px; display: flex; align-items: center; gap: 6px;">
                <i data-lucide="layers" style="width: 15px; height: 15px;"></i> 4. Proteção / Revestimentos
              </div>
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
                <div>
                  <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Revestimento Interno</label>
                  <input type="text" name="internalCoating" placeholder="Ex: Epóxi fenólico 500 μm" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                </div>
                <div>
                  <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Revestimento Externo</label>
                  <input type="text" name="externalCoating" placeholder="Ex: Primer epóxi + acabamento poliuretano" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                </div>
              </div>
            </div>

            <div>
              <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Observações Gerais</label>
              <textarea name="notes" rows="2" placeholder="Notas técnicas adicionais..." style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary); resize: vertical;"></textarea>
            </div>

            <div style="display: flex; justify-content: flex-end; gap: 8px; margin-top: 12px; border-top: 1px solid #27272a; padding-top: 16px;">
              <button type="button" id="btn-cancel-modal" class="btn-secondary-eq">Cancelar</button>
              <button type="submit" class="btn-primary-eq">Salvar Equipamento / Tubulação</button>
            </div>
          </form>
        </div>
      </div>
    `;

    createIcons({
      icons: { Plus, GitBranch, Boxes, Cpu, ShieldCheck, AlertTriangle },
      nameAttr: "data-lucide",
      root: modalContainer
    });

    PipingFormBuilder.bindPipingEvents(modalContainer);

    const clientSelect = modalContainer.querySelector('#create-eq-client-select') as HTMLSelectElement;
    const projectSelect = modalContainer.querySelector('#create-eq-project-select') as HTMLSelectElement;
    const unitAreaSelect = modalContainer.querySelector('#create-eq-unitarea-select') as HTMLSelectElement;
    const hierarchyAlert = modalContainer.querySelector('#create-eq-hierarchy-alert') as HTMLElement;
    const typeSelect = modalContainer.querySelector('#create-eq-type-select') as HTMLSelectElement;
    const pipingSection = modalContainer.querySelector('#create-piping-section') as HTMLElement;
    const pipingSelect = modalContainer.querySelector('#create-piping-system-ref') as HTMLSelectElement;
    const noPipingMsg = modalContainer.querySelector('#no-piping-systems-msg') as HTMLElement;
    const errorBanner = modalContainer.querySelector('#create-eq-error-banner') as HTMLElement;
    const errorText = modalContainer.querySelector('#create-eq-error-text') as HTMLElement;
    const genericThicknessField = modalContainer.querySelector('.generic-thickness-field input') as HTMLInputElement;

    const showError = (msg: string) => {
      if (errorBanner && errorText) {
        errorText.textContent = msg;
        errorBanner.style.display = 'flex';
        errorBanner.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    };

    clientSelect?.addEventListener('change', () => {
      if (errorBanner) errorBanner.style.display = 'none';
      const cId = clientSelect.value;
      if (!cId) {
        if (projectSelect) {
          projectSelect.innerHTML = '<option value="">Selecione o Cliente primeiro...</option>';
          projectSelect.disabled = true;
          projectSelect.style.opacity = '0.6';
        }
        if (unitAreaSelect) {
          unitAreaSelect.innerHTML = '<option value="">Selecione o Projeto primeiro...</option>';
          unitAreaSelect.disabled = true;
          unitAreaSelect.style.opacity = '0.6';
        }
        if (hierarchyAlert) hierarchyAlert.style.display = 'none';
        if (pipingSelect) {
          pipingSelect.innerHTML = '<option value="">Selecione o Cliente primeiro...</option>';
          pipingSelect.disabled = true;
          pipingSelect.style.opacity = '0.6';
        }
        if (noPipingMsg) noPipingMsg.style.display = 'none';
        return;
      }

      // Cascata 1: Projetos do cliente
      const clientProjects = this.projects.filter(p => p.clientId === cId && p.status !== 'CANCELLED');
      if (projectSelect) {
        if (clientProjects.length > 0) {
          projectSelect.disabled = false;
          projectSelect.style.opacity = '1';
          projectSelect.innerHTML = '<option value="">Selecione o Projeto...</option>' + 
            clientProjects.map(p => `<option value="${p.id}">${p.code} - ${p.name}</option>`).join('');
          if (hierarchyAlert) hierarchyAlert.style.display = 'none';
        } else {
          projectSelect.innerHTML = '<option value="">Nenhum projeto cadastrado</option>';
          projectSelect.disabled = true;
          projectSelect.style.opacity = '0.6';
          if (hierarchyAlert) {
            hierarchyAlert.innerHTML = '⚠️ <strong>Atenção:</strong> Este cliente não possui projetos cadastrados. Todo equipamento deve obrigatoriamente estar vinculado a um Projeto. <a href="#/management/projects" style="color: var(--ia-info-fg, #60a5fa); text-decoration: underline; font-weight: 600; margin-left: 4px;">Cadastrar Novo Projeto</a>';
            hierarchyAlert.style.display = 'block';
          }
        }
      }

      // Reseta Unidade/Área
      if (unitAreaSelect) {
        unitAreaSelect.innerHTML = '<option value="">Selecione o Projeto primeiro...</option>';
        unitAreaSelect.disabled = true;
        unitAreaSelect.style.opacity = '0.6';
      }

      // Cascata: Sistemas de tubulação (ST) do cliente
      const clientPipingSystems = this.equipments.filter(e => e.clientId === cId && e.equipmentType === 'PIPING_SYSTEM' && e.status !== 'INACTIVE');
      if (pipingSelect) {
        pipingSelect.disabled = false;
        pipingSelect.style.opacity = '1';
        let options = '<option value="">Selecione o Sistema de Tubulação (ST)...</option><option value="NOT_APPLICABLE">NÃO APLICÁVEL</option>';
        if (clientPipingSystems.length > 0) {
          options += clientPipingSystems.map(s => `<option value="${s.id}">${s.tag} - ${s.name}</option>`).join('');
          if (noPipingMsg) noPipingMsg.style.display = 'none';
        } else {
          if (noPipingMsg) noPipingMsg.style.display = 'block';
        }
        pipingSelect.innerHTML = options;
      }
    });

    // Cascata 2: Projeto -> Unidades/Áreas
    projectSelect?.addEventListener('change', () => {
      if (errorBanner) errorBanner.style.display = 'none';
      const pId = projectSelect.value;
      if (!pId) {
        if (unitAreaSelect) {
          unitAreaSelect.innerHTML = '<option value="">Selecione o Projeto primeiro...</option>';
          unitAreaSelect.disabled = true;
          unitAreaSelect.style.opacity = '0.6';
        }
        if (hierarchyAlert) hierarchyAlert.style.display = 'none';
        return;
      }

      const projectUnits = this.unitAreas.filter(u => u.projectId === pId && u.status !== 'INACTIVE');
      if (unitAreaSelect) {
        if (projectUnits.length > 0) {
          unitAreaSelect.disabled = false;
          unitAreaSelect.style.opacity = '1';
          unitAreaSelect.innerHTML = '<option value="">Selecione a Unidade / Área...</option>' + 
            projectUnits.map(u => `<option value="${u.id}">${u.code} - ${u.name}</option>`).join('');
          if (hierarchyAlert) hierarchyAlert.style.display = 'none';
        } else {
          unitAreaSelect.innerHTML = '<option value="">Nenhuma unidade/área cadastrada</option>';
          unitAreaSelect.disabled = true;
          unitAreaSelect.style.opacity = '0.6';
          if (hierarchyAlert) {
            hierarchyAlert.innerHTML = `⚠️ <strong>Atenção:</strong> O projeto selecionado não possui unidades/áreas cadastradas. Cadastre pelo menos uma unidade/área no projeto antes de vincular equipamentos. <a href="#/management/projects/${pId}" style="color: var(--ia-info-fg, #60a5fa); text-decoration: underline; font-weight: 600; margin-left: 4px;">Cadastrar Unidade no Projeto</a>`;
            hierarchyAlert.style.display = 'block';
          }
        }
      }
    });

    const togglePipingSection = () => {
      if (typeSelect && pipingSection) {
        const val = typeSelect.value;
        const isPiping = val === 'PIPING_LINE';
        const matContainer = modalContainer.querySelector('#create-material-container') as HTMLElement;
        const nomThickInput = modalContainer.querySelector('#create-nominal-thickness') as HTMLInputElement;

        if (isPiping) {
          pipingSection.style.display = 'block';
          if (matContainer) matContainer.style.display = 'none';
          if (nomThickInput) {
            nomThickInput.readOnly = true;
            nomThickInput.style.background = 'rgba(255,255,255,0.05)';
            nomThickInput.style.cursor = 'not-allowed';
            nomThickInput.style.color = 'var(--color-text-disabled)';
            const helper = modalContainer.querySelector('#create-nominal-thickness-helper') as HTMLElement;
            if (helper) helper.style.display = 'block';
            const label = nomThickInput.previousElementSibling as HTMLElement;
            if (label && !label.innerHTML.includes('lucide="lock"')) {
              label.style.display = 'flex';
              label.style.justifyContent = 'space-between';
              label.style.alignItems = 'center';
              label.innerHTML = `Espessura Nominal (mm) <i data-lucide="lock" style="width:12px; height:12px;"></i>`;
              if ((window as any).lucide) (window as any).lucide.createIcons({ root: label });
            }
          }
          if (pipingSelect && !pipingSelect.disabled) pipingSelect.required = true;
          if (pipingSelect && pipingSelect.options.length > 1 && !pipingSelect.value) {
            pipingSelect.selectedIndex = 1;
          }
        } else {
          pipingSection.style.display = 'none';
          if (matContainer) matContainer.style.display = 'block';
          if (nomThickInput) {
            nomThickInput.readOnly = false;
            nomThickInput.style.background = 'var(--color-input-bg)';
            nomThickInput.style.cursor = 'text';
            nomThickInput.style.color = 'var(--color-text-primary)';
            const helper = modalContainer.querySelector('#create-nominal-thickness-helper') as HTMLElement;
            if (helper) helper.style.display = 'none';
            const label = nomThickInput.previousElementSibling as HTMLElement;
            if (label && label.innerHTML.includes('lucide="lock"')) {
              label.style.display = 'block';
              label.innerHTML = `Espessura Nominal (mm)`;
            }
          }
          if (pipingSelect) {
            pipingSelect.required = false;
            pipingSelect.value = '';
          }
          if (genericThicknessField) {
            genericThicknessField.disabled = false;
            genericThicknessField.parentElement!.style.display = 'block';
          }
        }
      }
    };

    typeSelect?.addEventListener('change', togglePipingSection);

    const closeModal = () => {
      modalContainer.innerHTML = '';
      if (window.location.pathname === '/equipment/new' || window.location.hash.includes('/equipment/new')) {
        router.navigate('/equipment');
      }
    };
    modalContainer.querySelector('#btn-close-modal')?.addEventListener('click', closeModal);
    modalContainer.querySelector('#btn-cancel-modal')?.addEventListener('click', closeModal);

    const form = modalContainer.querySelector('#form-create-eq') as HTMLFormElement;
    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (errorBanner) errorBanner.style.display = 'none';
      const formData = new FormData(form);
      const clientId = (formData.get('clientId') as string)?.trim();
      const projectId = (formData.get('projectId') as string)?.trim();
      const unitAreaId = (formData.get('unitAreaId') as string)?.trim();

      if (!clientId) {
        showError("SELECIONE O CLIENTE. O cliente é obrigatório na hierarquia cadastral.");
        return;
      }
      if (!projectId) {
        showError("SELECIONE O PROJETO. O equipamento deve obrigatoriamente estar vinculado a um Projeto.");
        return;
      }
      if (!unitAreaId) {
        showError("SELECIONE A UNIDADE / ÁREA. O equipamento deve obrigatoriamente pertencer a uma Unidade/Área do Projeto.");
        return;
      }

      const eqType = formData.get('equipmentType') as EquipmentType;

      let resolvedSystem: string | null = null;
      let resolvedPipingSystemId: string | null = null;
      let resolvedPipingSystemApplicability: 'APPLICABLE' | 'NOT_APPLICABLE' | undefined = undefined;

      if (eqType === 'PIPING_LINE') {
        const selectedSysId = (formData.get('pipingSystemRef') as string) || '';
        if (!selectedSysId) {
          showError("Obrigatório: Selecione o Sistema de Tubulação ou marque 'Não Aplicável'.");
          return;
        }
        if (selectedSysId === 'NOT_APPLICABLE') {
          resolvedPipingSystemId = null;
          resolvedSystem = null;
          resolvedPipingSystemApplicability = 'NOT_APPLICABLE';
        } else {
          const foundSys = this.equipments.find(s => s.id === selectedSysId && s.clientId === clientId);
          if (!foundSys) {
            showError("Sistema de Tubulação inválido ou não pertence ao cliente selecionado.");
            return;
          }
          resolvedPipingSystemId = foundSys.id;
          resolvedSystem = foundSys.tag;
          resolvedPipingSystemApplicability = 'APPLICABLE';
        }
      }

      const parseMeas = (val: FormDataEntryValue | null, unitVal: FormDataEntryValue | null) => {
        if (!val || String(val).trim() === '') return null;
        const n = Number(val);
        if (isNaN(n)) return null;
        return { value: n, unit: String(unitVal || '') };
      };

      const designPressure = parseMeas(formData.get('designPressure'), formData.get('designPressureUnit'));
      const operatingPressure = parseMeas(formData.get('operatingPressure'), formData.get('operatingPressureUnit'));
      const designTemperature = parseMeas(formData.get('designTemperature'), formData.get('designTemperatureUnit'));
      const operatingTemperature = parseMeas(formData.get('operatingTemperature'), formData.get('operatingTemperatureUnit'));
      const nominalThickness = parseMeas(formData.get('nominalThickness'), 'mm');
      const corrosionAllowance = parseMeas(formData.get('corrosionAllowance'), 'mm');
      const pmtaValue = formData.get('pmtaValue') ? parseFloat(formData.get('pmtaValue') as string) : null;
      const pmtaUnit = (formData.get('pmtaUnit') as string) || null;

      let scheduleVal = (formData.get('schedule') as string) || null;
      let pipingDesignationVal = null;
      if (scheduleVal && scheduleVal.startsWith('{')) {
        try {
          const parsed = JSON.parse(scheduleVal);
          scheduleVal = parsed.schedule;
          pipingDesignationVal = parsed.designation;
        } catch {}
      }

      const pipingData = {
        nps: (formData.get('nps') as string) || null,
        schedule: scheduleVal,
        pipingDesignation: pipingDesignationVal,
        outsideDiameter: parseMeas(formData.get('outsideDiameter'), 'mm'),
        insulation: (formData.get('insulation') as string) || null,
        spec: (formData.get('spec') as string) || null,
        pressureClass: (formData.get('pressureClass') as string) || null,
        materialFamily: (formData.get('materialFamily') as string) || null,
        materialSpecification: (formData.get('materialSpecification') as string) || null,
        materialGrade: (formData.get('materialGrade') as string) || null,
        dimensionalStandard: (formData.get('dimensionalStandard') as string) || null,
        calculatedInnerDiameter: parseMeas(formData.get('calculatedInnerDiameter'), 'mm'),
        dimensionSource: (formData.get('dimensionSource') as 'CATALOG' | 'MANUAL') || 'CATALOG',
        dimensionManualSource: (formData.get('dimensionManualSource') as string) || null,
        dimensionManualJustification: (formData.get('dimensionManualJustification') as string) || null,
        lineNumber: (formData.get('lineNumber') as string) || null,
        fromReference: (formData.get('fromReference') as string) || null,
        toReference: (formData.get('toReference') as string) || null
      };

      const payload = {
        design: {
          pressure: designPressure,
          temperature: designTemperature,
          material: eqType === 'PIPING_LINE' ? `${pipingData.materialSpecification || ''} ${pipingData.materialGrade || ''}`.trim() || 'NÃO INFORMADO' : formData.get('material') || null,
          fabricationStandard: formData.get('fabricationStandard') || null,
          projectCode: formData.get('projectCode') || null,
          drawingReference: (formData.get('drawingReference') as string)?.trim() || null,
          flowDiagramReference: (formData.get('flowDiagramReference') as string)?.trim() || null,
          nominalThickness: nominalThickness,
          corrosionAllowance: corrosionAllowance
        },
        operation: {
          pressure: operatingPressure,
          temperature: operatingTemperature,
          fluid: formData.get('fluid') || null,
          fluidClass: formData.get('fluidClass') || null
        },
        inService: {
          inspectionCode: formData.get('inServiceInspectionCode') || null
        },
        pipingLines: []
      };

      const newEquipment = {
        id: `EQ-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`.toUpperCase() as any,
        clientId: clientId as any,
        projectId: projectId as any,
        unitAreaId: unitAreaId as any,
        tag: (formData.get('tag') as string).trim().toUpperCase(),
        name: formData.get('name') as string,
        description: null,
        equipmentType: eqType,
        areaId: unitAreaId as any,
        system: resolvedSystem,
        pipeSystemApplicability: resolvedPipingSystemApplicability,
        pipingSystemId: resolvedPipingSystemId,
        subsystem: null,
        service: (formData.get('fluid') as string) || (formData.get('name') as string) || null,
        manufacturer: (formData.get('manufacturer') as string) || null,
        model: null,
        serialNumber: (formData.get('serialNumber') as string) || null,
        yearOfManufacture: formData.get('yearOfManufacture') ? Number(formData.get('yearOfManufacture')) : null,
        installationDate: null,
        commissioningDate: null,
        criticality: (formData.get('criticality') as any) || 'MEDIUM',
        location: null,
        notes: (formData.get('notes') as string) || null,
        status: 'ACTIVE',

        ...(eqType === 'PIPING_LINE' ? {
          nps: pipingData.nps,
          schedule: pipingData.schedule,
          pipingDesignation: pipingData.pipingDesignation,
          outsideDiameter: pipingData.outsideDiameter,
          insulation: pipingData.insulation,
          spec: pipingData.spec,
          pressureClass: pipingData.pressureClass,
          materialFamily: pipingData.materialFamily,
          materialSpecification: pipingData.materialSpecification,
          materialGrade: pipingData.materialGrade,
          dimensionalStandard: pipingData.dimensionalStandard,
          calculatedInnerDiameter: pipingData.calculatedInnerDiameter,
          dimensionSource: pipingData.dimensionSource,
          dimensionManualSource: pipingData.dimensionManualSource,
          dimensionManualJustification: pipingData.dimensionManualJustification,
          lineNumber: pipingData.lineNumber,
          fromReference: pipingData.fromReference,
          toReference: pipingData.toReference
        } : {
          lineNumber: null,
          fromReference: null,
          toReference: null,
          nps: null,
          schedule: null,
          pipingDesignation: null,
          outsideDiameter: null,
          insulation: null,
          spec: null,
          pressureClass: null,
          materialFamily: null,
          materialSpecification: null,
          materialGrade: null,
          dimensionalStandard: null,
          calculatedInnerDiameter: null,
          dimensionSource: undefined,
          dimensionManualSource: null,
          dimensionManualJustification: null
        }),

        material: eqType === 'PIPING_LINE' ? `${pipingData.materialSpecification || ''} ${pipingData.materialGrade || ''}`.trim() || 'NÃO INFORMADO' : (formData.get('material') as string) || null,
        fluid: (formData.get('fluid') as string) || null,
        fluidClass: (formData.get('fluidClass') as string) || null,
        designPressure: designPressure,
        operatingPressure: operatingPressure,
        designTemperature: designTemperature,
        pmtaValue: pmtaValue,
        pmtaUnit: pmtaUnit,
        operatingTemperature: operatingTemperature,
        nominalThickness: nominalThickness,
        corrosionAllowance: corrosionAllowance,
        fabricationStandard: (formData.get('fabricationStandard') as string) || null,
        projectCode: (formData.get('projectCode') as string) || null,
        drawingReference: (formData.get('drawingReference') as string)?.trim() || null,
        flowDiagramReference: (formData.get('flowDiagramReference') as string)?.trim() || null,
        inServiceInspectionCode: (formData.get('inServiceInspectionCode') as string) || null,
        nr13Applicability: (formData.get('nr13Applicability') as any) || 'UNCLASSIFIED',
        nr13Category: (formData.get('nr13Category') as string) || null,
        internalCoating: (formData.get('internalCoating') as string) || null,
        externalCoating: (formData.get('externalCoating') as string) || null,

        payloadJson: JSON.stringify(payload),
        version: 1,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      } as Equipment;

      try {
        const dummyContext = { activeClientId: clientId, permissions: contextStore.get().permissions, correlationId: `cid-${Date.now()}`, userId: contextStore.get().userId || 'USER-1' } as any;
        await window.app.platform.equipmentService.createEquipment(dummyContext, newEquipment);
        closeModal();
        this.loadData();
      } catch (err: any) {
        showError(`Erro ao criar equipamento: ${err.message}`);
      }
    });
  }

  // EDIT MODAL
  private async openEditModal(eq: Equipment) {
    const modalContainer = this.querySelector('#eq-modal-container') as HTMLElement;
    if (!modalContainer) return;

    const client = this.clients.find(c => c.id === eq.clientId);
    const clientName = client ? (client.name || client.legalName) : (eq.clientId || '-');
    const clientProjects = this.projects.filter(p => p.clientId === eq.clientId && p.status !== 'CANCELLED');
    const currentProjectId = eq.projectId || (this.unitAreas.find(u => u.id === (eq.unitAreaId || eq.areaId))?.projectId) || (clientProjects[0]?.id || '');
    const projectUnits = this.unitAreas.filter(u => u.projectId === currentProjectId && u.status !== 'INACTIVE');
    const currentUnitAreaId = eq.unitAreaId || eq.areaId || (projectUnits[0]?.id || '');

    const pipingSystems = this.equipments.filter(e => e.equipmentType === 'PIPING_SYSTEM' && e.status !== 'INACTIVE' && e.id !== eq.id);
    const initialPipingSysId = eq.pipingSystemId || (pipingSystems.find(s => s.tag === eq.system)?.id || '');
    
    const pipingEngineeringHtml = await PipingFormBuilder.renderPipingFields(eq, this.equipments);

    modalContainer.innerHTML = `
      <div class="modal-backdrop" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.8); z-index: 999; display: flex; align-items: center; justify-content: center; padding: 20px;">
        <div style="background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 8px; width: 100%; max-width: 820px; max-height: 92vh; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.5);">
          <div style="padding: 16px 20px; border-bottom: 1px solid var(--ia-line, #27272a); display: flex; justify-content: space-between; align-items: center; background: var(--ia-bg-panel, #202024);">
            <h3 style="margin: 0; font-size: 1.15rem; color: var(--color-text-primary); display: flex; align-items: center; gap: 8px;">
              <i data-lucide="edit-3" style="color: var(--ia-info-fg, #60a5fa); width: 20px; height: 20px;"></i> Editar Ativo (${this.escapeHtml(eq.tag)})
            </h3>
            <button id="btn-close-modal" style="background: transparent; border: none; color: var(--color-text-disabled); font-size: 1.2rem; cursor: pointer;">✕</button>
          </div>
          
          <form id="form-edit-eq" style="padding: 20px; overflow-y: auto; display: flex; flex-direction: column; gap: 16px;">
            <!-- SEÇÃO 1: IDENTIFICAÇÃO E HIERARQUIA CADASTRAL OBRIGATÓRIA -->
            <div style="background: var(--ia-bg-panel, #121215); border: 1px solid var(--ia-line, #27272a); border-radius: 6px; padding: 14px;">
              <div style="font-size: 0.85rem; font-weight: 600; color: var(--ia-info-fg, #60a5fa); margin-bottom: 12px; display: flex; align-items: center; gap: 6px;">
                <i data-lucide="boxes" style="width: 15px; height: 15px;"></i> 1. Identificação Geral & Hierarquia Organizacional
              </div>

              <!-- HIERARQUIA OBRIGATÓRIA: CLIENTE -> PROJETO -> UNIDADE/ÁREA -->
              <div style="background: rgba(37, 99, 235, 0.08); border: 1px solid rgba(59, 130, 246, 0.25); border-radius: 6px; padding: 12px; margin-bottom: 14px;">
                <div style="font-size: 0.78rem; font-weight: 700; color: var(--color-border-default); margin-bottom: 10px; text-transform: uppercase; letter-spacing: 0.05em; display: flex; align-items: center; gap: 6px;">
                  <i data-lucide="git-branch" style="width: 14px; height: 14px;"></i> Hierarquia Organizacional Obrigatória: Cliente &rarr; Projeto &rarr; Unidade/Área
                </div>

                <div style="margin-bottom: 10px;">
                  <label style="display: block; font-size: 0.8rem; color: var(--color-graphite-300); margin-bottom: 4px; font-weight: 600;">1. Cliente</label>
                  <div style="width: 100%; background: var(--color-input-bg); border: 1px solid #27272a; border-radius: 6px; padding: 8px 12px; color: var(--color-border-default); font-weight: 600;">
                    <i data-lucide="building-2" style="width: 1em; height: 1em; display: inline-block; vertical-align: middle; margin-top: -2px; margin-right: 4px;"></i> ${this.escapeHtml(clientName)}
                  </div>
                  <input type="hidden" name="clientId" value="${eq.clientId}">
                </div>

                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
                  <div>
                    <label style="display: block; font-size: 0.8rem; color: var(--color-graphite-300); margin-bottom: 4px; font-weight: 600;">2. Projeto Vinculado *</label>
                    <select name="projectId" id="edit-eq-project-select" required style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                      <option value="">Selecione o Projeto...</option>
                      ${clientProjects.map(p => `<option value="${p.id}" ${p.id === currentProjectId ? 'selected' : ''}>${p.code} - ${p.name}</option>`).join('')}
                    </select>
                  </div>
                  <div>
                    <label style="display: block; font-size: 0.8rem; color: var(--color-graphite-300); margin-bottom: 4px; font-weight: 600;">3. Unidade / Área do Projeto *</label>
                    <select name="unitAreaId" id="edit-eq-unitarea-select" required style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                      <option value="">Selecione a Unidade / Área...</option>
                      ${projectUnits.map(u => `<option value="${u.id}" ${u.id === currentUnitAreaId ? 'selected' : ''}>${u.code} - ${u.name}</option>`).join('')}
                    </select>
                  </div>
                </div>

                <div id="edit-eq-hierarchy-alert" style="display: none; margin-top: 10px; background: rgba(245, 158, 11, 0.15); border: 1px solid rgba(245, 158, 11, 0.4); color: var(--status-warning-text); padding: 10px 12px; border-radius: 6px; font-size: 0.8rem; line-height: 1.4;">
                </div>
              </div>

              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
                <div>
                  <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">TAG do Ativo *</label>
                  <input type="text" name="tag" required value="${this.escapeHtml(eq.tag)}" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary); font-family: monospace; font-weight: 600;">
                </div>
                <div>
                  <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Tipo de Ativo *</label>
                  <select name="equipmentType" id="edit-eq-type-select" required style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                    <option value="PRESSURE_VESSEL" ${eq.equipmentType === 'PRESSURE_VESSEL' ? 'selected' : ''}>Vaso de Pressão (VP)</option>
                    <option value="STORAGE_TANK" ${eq.equipmentType === 'STORAGE_TANK' ? 'selected' : ''}>Tanque de Armazenamento (TQ)</option>
                    <option value="HEAT_EXCHANGER" ${eq.equipmentType === 'HEAT_EXCHANGER' ? 'selected' : ''}>Permutador de Calor (TC)</option>
                    <option value="BOILER" ${eq.equipmentType === 'BOILER' ? 'selected' : ''}>Caldeira (CD)</option>
                    <option value="FURNACE" ${eq.equipmentType === 'FURNACE' ? 'selected' : ''}>Forno Industrial (FN)</option>
                    <option value="PIPING_SYSTEM" ${eq.equipmentType === 'PIPING_SYSTEM' ? 'selected' : ''}>Sistema de Tubulação (ST)</option>
                    <option value="PIPING_LINE" ${eq.equipmentType === 'PIPING_LINE' ? 'selected' : ''}>Tubulação</option>
                    <option value="OTHER" ${eq.equipmentType === 'OTHER' ? 'selected' : ''}>Outro</option>
                  </select>
                </div>
              </div>

              <div style="margin-top: 10px;">
                <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Nome / Descrição Resumida *</label>
                <input type="text" name="name" required value="${this.escapeHtml(eq.name)}" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
              </div>

              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-top: 10px;">
                <div>
                  <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Fabricante</label>
                  <input type="text" name="manufacturer" value="${this.escapeHtml(eq.manufacturer || '')}" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                </div>
                <div>
                  <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Ano Fabricação</label>
                  <input type="number" name="yearOfManufacture" value="${this.escapeHtml(eq.yearOfManufacture || '')}" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                </div>
              </div>
            </div>

            <!-- SEÇÃO ESPECÍFICA DE TUBULAÇÃO (PIPING_LINE) -->
            <div id="edit-piping-section" style="display: ${eq.equipmentType === 'PIPING_LINE' ? 'block' : 'none'}; background: rgba(59, 130, 246, 0.05); border: 1px solid rgba(59, 130, 246, 0.25); border-radius: 6px; padding: 14px; margin-bottom: 14px;">
              <div style="font-size: 0.85rem; font-weight: 600; color: var(--color-border-default); margin-bottom: 12px; display: flex; align-items: center; gap: 6px;">
                <i data-lucide="git-branch" style="width: 15px; height: 15px;"></i> Especificidades de Tubulação & DE / PARA
              </div>

              <div style="margin-bottom: 12px;">
                <label style="display: block; font-size: 0.8rem; color: var(--color-border-default); font-weight: 600; margin-bottom: 4px;">
                  Sistema de Tubulação (ST) Vinculado *
                </label>
                <select name="pipingSystemRef" id="edit-piping-system-ref" ${eq.equipmentType === 'PIPING_LINE' ? 'required' : ''} style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-ink); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                  <option value="">Selecione o Sistema de Tubulação (ST)...</option>
                  <option value="NOT_APPLICABLE" ${eq.pipeSystemApplicability === 'NOT_APPLICABLE' ? 'selected' : ''}>NÃO APLICÁVEL</option>
                  ${pipingSystems.filter(s => s.clientId === eq.clientId).map(s => `<option value="${s.id}" ${initialPipingSysId === s.id ? 'selected' : ''}>${s.tag} - ${s.name}</option>`).join('')}
                </select>
              </div>

              ${pipingEngineeringHtml}
            </div>

            <!-- SEÇÃO 2: PROJETO VS OPERAÇÃO -->
            <div style="background: var(--ia-bg-panel, #121215); border: 1px solid var(--ia-line, #27272a); border-radius: 6px; padding: 14px;">
              <div style="font-size: 0.85rem; font-weight: 600; color: var(--color-text-muted); margin-bottom: 12px; display: flex; align-items: center; gap: 6px;">
                <i data-lucide="cpu" style="width: 15px; height: 15px;"></i> 2. Parâmetros de Projeto vs Operação (Estritamente Separados)
              </div>

              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 14px;">
                <!-- PROJETO -->
                <div style="background: var(--color-input-bg); border: 1px solid #27272a; border-radius: 6px; padding: 12px;">
                  <div style="font-size: 0.8rem; font-weight: 700; color: var(--ia-info-fg, #60a5fa); margin-bottom: 8px;">Dados de Projeto</div>
                  <div style="display: flex; gap: 6px; margin-bottom: 8px;">
                    <div style="flex: 2;">
                      <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Pressão de Projeto</label>
                      <input type="number" step="0.01" name="designPressure" value="${eq.designPressure?.value ?? ''}" placeholder="Ex: 15.5" style="width: 100%; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 8px; color: var(--color-text-primary);">
                    </div>
                    <div style="flex: 1;">
                      <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Unidade</label>
                      <select name="designPressureUnit" style="width: 100%; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 8px; color: var(--color-text-primary);">
                        <option value="kgf/cm²" ${eq.designPressure?.unit === 'kgf/cm²' ? 'selected' : ''}>kgf/cm²</option>
                        <option value="bar" ${eq.designPressure?.unit === 'bar' ? 'selected' : ''}>bar</option>
                        <option value="MPa" ${eq.designPressure?.unit === 'MPa' ? 'selected' : ''}>MPa</option>
                        <option value="psi" ${eq.designPressure?.unit === 'psi' ? 'selected' : ''}>psi</option>
                      </select>
                    </div>
                  </div>
                  <div style="display: flex; gap: 6px; margin-bottom: 8px;">
                    <div style="flex: 2;">
                      <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Temperatura de Projeto</label>
                      <input type="number" step="0.1" name="designTemperature" value="${eq.designTemperature?.value ?? ''}" placeholder="Ex: 60" style="width: 100%; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 8px; color: var(--color-text-primary);">
                    </div>
                    <div style="flex: 1;">
                      <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Unidade</label>
                      <select name="designTemperatureUnit" style="width: 100%; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 8px; color: var(--color-text-primary);">
                        <option value="°C" ${eq.designTemperature?.unit === '°C' ? 'selected' : ''}>°C</option>
                        <option value="°F" ${eq.designTemperature?.unit === '°F' ? 'selected' : ''}>°F</option>
                      </select>
                    </div>
                  </div>
                  <div style="display: flex; gap: 6px; margin-bottom: 8px;">
                    <div style="flex: 2;">
                      <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">PMTA</label>
                      <input type="number" step="0.1" name="pmtaValue" value="${eq.pmtaValue ?? ''}" placeholder="Ex: 10" style="width: 100%; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 8px; color: var(--color-text-primary);">
                    </div>
                    <div style="flex: 1;">
                      <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Unidade</label>
                      <select name="pmtaUnit" style="width: 100%; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 8px; color: var(--color-text-primary);">
                        <option value="kgf/cm²" ${eq.pmtaUnit === 'kgf/cm²' ? 'selected' : ''}>kgf/cm²</option>
                        <option value="bar" ${eq.pmtaUnit === 'bar' ? 'selected' : ''}>bar</option>
                        <option value="MPa" ${eq.pmtaUnit === 'MPa' ? 'selected' : ''}>MPa</option>
                        <option value="psi" ${eq.pmtaUnit === 'psi' ? 'selected' : ''}>psi</option>
                      </select>
                    </div>
                  </div>
                  <div id="edit-material-container" style="display: ${eq.equipmentType === 'PIPING_LINE' ? 'none' : 'block'};">
                    <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Material de Construção</label>
                    <input type="text" name="material" value="${eq.material || ''}" placeholder="Ex: ASTM A106 Gr B" style="width: 100%; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 8px; color: var(--color-text-primary);">
                  </div>
                  <div style="margin-top: 8px; display: grid; grid-template-columns: 1fr 1fr; gap: 8px;">
                    <div>
                      <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Desenho de Referência</label>
                      <input type="text" name="drawingReference" value="${eq.drawingReference ?? eq.projectDrawingFlowchart ?? ''}" placeholder="Ex: DWG-3201-001" style="width: 100%; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 8px; color: var(--color-text-primary);">
                    </div>
                    <div>
                      <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Fluxograma (P&ID/PFD)</label>
                      <input type="text" name="flowDiagramReference" value="${eq.flowDiagramReference ?? eq.projectDrawingFlowchart ?? ''}" placeholder="Ex: P&ID-3201-001" style="width: 100%; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 8px; color: var(--color-text-primary);">
                    </div>
                  </div>
                </div>

                <!-- OPERAÇÃO -->
                <div style="background: var(--color-input-bg); border: 1px solid #27272a; border-radius: 6px; padding: 12px;">
                  <div style="font-size: 0.8rem; font-weight: 700; color: var(--status-success-text); margin-bottom: 8px;">Dados de Operação</div>
                  <div style="display: flex; gap: 6px; margin-bottom: 8px;">
                    <div style="flex: 2;">
                      <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Pressão de Operação</label>
                      <input type="number" step="0.01" name="operatingPressure" value="${eq.operatingPressure?.value ?? ''}" placeholder="Ex: 12.0" style="width: 100%; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 8px; color: var(--color-text-primary);">
                    </div>
                    <div style="flex: 1;">
                      <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Unidade</label>
                      <select name="operatingPressureUnit" style="width: 100%; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 8px; color: var(--color-text-primary);">
                        <option value="kgf/cm²" ${eq.operatingPressure?.unit === 'kgf/cm²' ? 'selected' : ''}>kgf/cm²</option>
                        <option value="bar" ${eq.operatingPressure?.unit === 'bar' ? 'selected' : ''}>bar</option>
                        <option value="MPa" ${eq.operatingPressure?.unit === 'MPa' ? 'selected' : ''}>MPa</option>
                        <option value="psi" ${eq.operatingPressure?.unit === 'psi' ? 'selected' : ''}>psi</option>
                      </select>
                    </div>
                  </div>
                  <div style="display: flex; gap: 6px; margin-bottom: 8px;">
                    <div style="flex: 2;">
                      <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Temperatura de Operação</label>
                      <input type="number" step="0.1" name="operatingTemperature" value="${eq.operatingTemperature?.value ?? ''}" placeholder="Ex: -15" style="width: 100%; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 8px; color: var(--color-text-primary);">
                    </div>
                    <div style="flex: 1;">
                      <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Unidade</label>
                      <select name="operatingTemperatureUnit" style="width: 100%; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 8px; color: var(--color-text-primary);">
                        <option value="°C" ${eq.operatingTemperature?.unit === '°C' ? 'selected' : ''}>°C</option>
                        <option value="°F" ${eq.operatingTemperature?.unit === '°F' ? 'selected' : ''}>°F</option>
                      </select>
                    </div>
                  </div>
                  <div style="display: grid; grid-template-columns: 1.5fr 1fr; gap: 6px;">
                    <div>
                      <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Fluido</label>
                      <input type="text" name="fluid" value="${eq.fluid || eq.service || ''}" placeholder="Ex: Amônia Anidra, Vapor" style="width: 100%; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 8px; color: var(--color-text-primary);">
                    </div>
                    <div>
                      <label style="display: block; font-size: 0.75rem; color: var(--color-text-disabled); margin-bottom: 2px;">Classe Fluido</label>
                      <select name="fluidClass" style="width: 100%; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 4px; padding: 6px 8px; color: var(--color-text-primary);">
                        <option value="" ${!eq.fluidClass ? 'selected' : ''}>Não clas.</option>
                        <option value="Classe A" ${eq.fluidClass === 'Classe A' ? 'selected' : ''}>Classe A</option>
                        <option value="Classe B" ${eq.fluidClass === 'Classe B' ? 'selected' : ''}>Classe B</option>
                        <option value="Classe C" ${eq.fluidClass === 'Classe C' ? 'selected' : ''}>Classe C</option>
                        <option value="Classe D" ${eq.fluidClass === 'Classe D' ? 'selected' : ''}>Classe D</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- SEÇÃO 3: INTEGRIDADE, ESPESSURAS E NORMAS -->
            <div style="background: var(--ia-bg-panel, #121215); border: 1px solid var(--ia-line, #27272a); border-radius: 6px; padding: 14px;">
              <div style="font-size: 0.85rem; font-weight: 600; color: var(--status-warning-text); margin-bottom: 12px; display: flex; align-items: center; gap: 6px;">
                <i data-lucide="shield-check" style="width: 15px; height: 15px;"></i> 3. Espessuras, Normas e Enquadramento NR-13
              </div>

              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 12px;">
                <div class="generic-thickness-field" style="position: relative;">
                  <label style="display: flex; justify-content: space-between; align-items: center; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Espessura Nominal (mm) ${eq.equipmentType === 'PIPING_LINE' ? '<i data-lucide="lock" style="width:12px; height:12px;"></i>' : ''}</label>
                  <input type="number" step="0.01" name="nominalThickness" id="edit-nominal-thickness" value="${eq.nominalThickness?.value ?? ''}" ${eq.equipmentType === 'PIPING_LINE' ? 'readonly style="width: 100%; background: rgba(255,255,255,0.05); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-disabled); cursor: not-allowed;"' : 'placeholder="Ex: 6.02" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);"'}>
                  ${eq.equipmentType === 'PIPING_LINE' ? '<div id="edit-nominal-thickness-helper" style="display: block; font-size: 0.7rem; color: var(--color-text-disabled); margin-top: 4px;">Definida pelo Cadastro Técnico Assistido de Tubulação</div>' : ''}
                </div>
                <div>
                  <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Sobre-espessura de Corrosão (mm)</label>
                  <input type="number" step="0.01" name="corrosionAllowance" value="${eq.corrosionAllowance?.value ?? ''}" placeholder="Ex: 1.50" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                </div>
              </div>

              <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; margin-bottom: 12px;">
                <div>
                  <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Norma de Fabricação / Projeto</label>
                  <input type="text" name="fabricationStandard" id="edit-fabrication-standard" value="${eq.fabricationStandard || ''}" placeholder="Ex: ASME VIII Div 1" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                </div>
                <div class="generic-projectcode-field" style="position: relative;">
                  <label style="display: flex; justify-content: space-between; align-items: center; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Código de Projeto ${eq.equipmentType === 'PIPING_LINE' ? '<i data-lucide="lock" style="width:12px; height:12px;"></i>' : ''}</label>
                  <input type="text" name="projectCode" id="edit-project-code" value="${eq.projectCode || ''}" ${eq.equipmentType === 'PIPING_LINE' ? 'readonly style="width: 100%; background: rgba(255,255,255,0.05); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-disabled); cursor: not-allowed;"' : 'placeholder="Ex: ASME B31.3" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);"'}>
                  ${eq.equipmentType === 'PIPING_LINE' ? '<div id="edit-project-code-helper" style="display: block; font-size: 0.7rem; color: var(--color-text-disabled); margin-top: 4px;">Definido pelo Cadastro Técnico Assistido de Tubulação</div>' : ''}
                </div>
                <div>
                  <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Código de Inspeção em Serviço</label>
                  <input type="text" name="inServiceInspectionCode" value="${eq.inServiceInspectionCode || ''}" placeholder="Ex: API 570, API 510" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                </div>
              </div>

              <div style="display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 10px; background: var(--color-input-bg); border: 1px solid #27272a; padding: 10px; border-radius: 6px;">
                <div>
                  <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Aplicabilidade NR-13</label>
                  <select name="nr13Applicability" id="edit-nr13-applicability" style="width: 100%; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                    <option value="APPLICABLE" ${eq.nr13Applicability === 'APPLICABLE' ? 'selected' : ''}>Aplicável</option>
                    <option value="NOT_APPLICABLE" ${eq.nr13Applicability === 'NOT_APPLICABLE' ? 'selected' : ''}>Não Aplicável</option>
                    <option value="UNCLASSIFIED" ${!eq.nr13Applicability || eq.nr13Applicability === 'UNCLASSIFIED' ? 'selected' : ''}>Não Classificado</option>
                  </select>
                </div>
                <div>
                  <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Categoria NR-13</label>
                  <select name="nr13Category" id="edit-nr13-category" style="width: 100%; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                    <option value="" ${!eq.nr13Category ? 'selected' : ''}>Sem Cat.</option>
                    <option value="I" ${eq.nr13Category === 'I' ? 'selected' : ''}>Categoria I</option>
                    <option value="II" ${eq.nr13Category === 'II' ? 'selected' : ''}>Categoria II</option>
                    <option value="III" ${eq.nr13Category === 'III' ? 'selected' : ''}>Categoria III</option>
                    <option value="IV" ${eq.nr13Category === 'IV' ? 'selected' : ''}>Categoria IV</option>
                    <option value="V" ${eq.nr13Category === 'V' ? 'selected' : ''}>Categoria V</option>
                  </select>
                </div>
                <div>
                  <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Criticidade Geral</label>
                  <select name="criticality" style="width: 100%; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                    <option value="MEDIUM" ${eq.criticality === 'MEDIUM' ? 'selected' : ''}>Média</option>
                    <option value="LOW" ${eq.criticality === 'LOW' ? 'selected' : ''}>Baixa</option>
                    <option value="HIGH" ${eq.criticality === 'HIGH' ? 'selected' : ''}>Alta</option>
                    <option value="CRITICAL" ${eq.criticality === 'CRITICAL' ? 'selected' : ''}>Crítica</option>
                  </select>
                </div>
                <div>
                  <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Status</label>
                  <select name="status" style="width: 100%; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                    <option value="ACTIVE" ${eq.status === 'ACTIVE' ? 'selected' : ''}>Ativo</option>
                    <option value="OUT_OF_SERVICE" ${eq.status === 'OUT_OF_SERVICE' ? 'selected' : ''}>Fora de Serviço</option>
                    <option value="INACTIVE" ${eq.status === 'INACTIVE' ? 'selected' : ''}>Inativo</option>
                  </select>
                </div>
              </div>
            </div>

            <!-- SEÇÃO 4: PROTEÇÃO / REVESTIMENTOS -->
            <div style="background: var(--ia-bg-panel, #121215); border: 1px solid var(--ia-line, #27272a); border-radius: 6px; padding: 14px; margin-bottom: 14px;">
              <div style="font-size: 0.85rem; font-weight: 600; color: var(--ia-info-fg, #a78bfa); margin-bottom: 12px; display: flex; align-items: center; gap: 6px;">
                <i data-lucide="layers" style="width: 15px; height: 15px;"></i> 4. Proteção / Revestimentos
              </div>
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
                <div>
                  <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Revestimento Interno</label>
                  <input type="text" name="internalCoating" value="${this.escapeHtml(eq.internalCoating || '')}" placeholder="Ex: Epóxi fenólico 500 μm" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                </div>
                <div>
                  <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Revestimento Externo</label>
                  <input type="text" name="externalCoating" value="${this.escapeHtml(eq.externalCoating || '')}" placeholder="Ex: Primer epóxi + acabamento poliuretano" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary);">
                </div>
              </div>
            </div>

            <div>
              <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Observações</label>
              <textarea name="notes" rows="2" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary); resize: vertical;">${eq.notes || ''}</textarea>
            </div>

            <div style="display: flex; justify-content: flex-end; gap: 8px; margin-top: 12px; border-top: 1px solid #27272a; padding-top: 16px;">
              <button type="button" id="btn-cancel-modal" class="btn-secondary-eq">Cancelar</button>
              <button type="submit" class="btn-primary-eq">Atualizar Ativo</button>
            </div>
          </form>
        </div>
      </div>
    `;

    createIcons({
      icons: { Edit3, GitBranch, Boxes, Cpu, ShieldCheck },
      nameAttr: "data-lucide",
      root: modalContainer
    });
    
    PipingFormBuilder.bindPipingEvents(modalContainer);

    const projectSelect = modalContainer.querySelector('#edit-eq-project-select') as HTMLSelectElement;
    const unitAreaSelect = modalContainer.querySelector('#edit-eq-unitarea-select') as HTMLSelectElement;

    projectSelect?.addEventListener('change', () => {
      const pId = projectSelect.value;
      if (!pId) {
        if (unitAreaSelect) {
          unitAreaSelect.innerHTML = '<option value="">Selecione o Projeto primeiro...</option>';
          unitAreaSelect.disabled = true;
          unitAreaSelect.style.opacity = '0.6';
        }
        return;
      }
      const units = this.unitAreas.filter(u => u.projectId === pId && u.status !== 'INACTIVE');
      if (unitAreaSelect) {
        unitAreaSelect.disabled = false;
        unitAreaSelect.style.opacity = '1';
        if (units.length > 0) {
          unitAreaSelect.innerHTML = '<option value="">Selecione a Unidade / Área...</option>' +
            units.map(u => `<option value="${u.id}">${u.code} - ${u.name}</option>`).join('');
        } else {
          unitAreaSelect.innerHTML = '<option value="">Nenhuma unidade cadastrada neste projeto</option>';
        }
      }
    });

    const typeSelect = modalContainer.querySelector('#edit-eq-type-select') as HTMLSelectElement;
    const pipingSection = modalContainer.querySelector('#edit-piping-section') as HTMLElement;
    const pipingSelect = modalContainer.querySelector('#edit-piping-system-ref') as HTMLSelectElement;

    const togglePipingSection = () => {
      if (typeSelect && pipingSection) {
        const val = typeSelect.value;
        const isPiping = val === 'PIPING_LINE';
        const matContainer = modalContainer.querySelector('#edit-material-container') as HTMLElement;
        const nomThickInput = modalContainer.querySelector('#edit-nominal-thickness') as HTMLInputElement;

        if (isPiping) {
          pipingSection.style.display = 'block';
          if (matContainer) matContainer.style.display = 'none';
          if (nomThickInput) {
            nomThickInput.readOnly = true;
            nomThickInput.style.background = 'rgba(255,255,255,0.05)';
            nomThickInput.style.cursor = 'not-allowed';
            nomThickInput.style.color = 'var(--color-text-disabled)';
            const helper = modalContainer.querySelector('#edit-nominal-thickness-helper') as HTMLElement;
            if (helper) helper.style.display = 'block';
            const label = nomThickInput.previousElementSibling as HTMLElement;
            if (label && !label.innerHTML.includes('lucide="lock"')) {
              label.style.display = 'flex';
              label.style.justifyContent = 'space-between';
              label.style.alignItems = 'center';
              label.innerHTML = `Espessura Nominal (mm) <i data-lucide="lock" style="width:12px; height:12px;"></i>`;
              if ((window as any).lucide) (window as any).lucide.createIcons({ root: label });
            }
          }
          if (pipingSelect) pipingSelect.required = true;
        } else {
          pipingSection.style.display = 'none';
          if (matContainer) matContainer.style.display = 'block';
          if (nomThickInput) {
            nomThickInput.readOnly = false;
            nomThickInput.style.background = 'var(--color-input-bg)';
            nomThickInput.style.cursor = 'text';
            nomThickInput.style.color = 'var(--color-text-primary)';
            const helper = modalContainer.querySelector('#edit-nominal-thickness-helper') as HTMLElement;
            if (helper) helper.style.display = 'none';
            const label = nomThickInput.previousElementSibling as HTMLElement;
            if (label && label.innerHTML.includes('lucide="lock"')) {
              label.style.display = 'block';
              label.innerHTML = `Espessura Nominal (mm)`;
            }
          }
          if (pipingSelect) {
            pipingSelect.required = false;
            pipingSelect.value = '';
          }
        }
      }
    };

    typeSelect?.addEventListener('change', togglePipingSection);

    const closeModal = () => { modalContainer.innerHTML = ''; };
    modalContainer.querySelector('#btn-close-modal')?.addEventListener('click', closeModal);
    modalContainer.querySelector('#btn-cancel-modal')?.addEventListener('click', closeModal);

    const form = modalContainer.querySelector('#form-edit-eq') as HTMLFormElement;
    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const formData = new FormData(form);
      const activeClientId = eq.clientId || contextStore.get().activeClientId;
      const projectId = (formData.get('projectId') as string)?.trim();
      const unitAreaId = (formData.get('unitAreaId') as string)?.trim();

      if (!projectId) {
        alert("Obrigatório: Selecione o Projeto ao qual este equipamento pertence.");
        return;
      }
      if (!unitAreaId) {
        alert("Obrigatório: Selecione a Unidade / Área à qual este equipamento pertence.");
        return;
      }

      const eqType = formData.get('equipmentType') as EquipmentType;

      let resolvedSystem: string | null = eq.system;
      let resolvedPipingSystemId: string | null = eq.pipingSystemId || null;
      let resolvedPipingSystemApplicability: 'APPLICABLE' | 'NOT_APPLICABLE' | undefined = eq.pipeSystemApplicability;

      if (eqType === 'PIPING_LINE') {
        const selectedSysId = (formData.get('pipingSystemRef') as string) || '';
        if (!selectedSysId) {
          alert("Obrigatório: Selecione o Sistema de Tubulação ou marque 'Não Aplicável'.");
          return;
        }
        if (selectedSysId === 'NOT_APPLICABLE') {
          resolvedPipingSystemId = null;
          resolvedSystem = null;
          resolvedPipingSystemApplicability = 'NOT_APPLICABLE';
        } else {
          const foundSys = pipingSystems.find(s => s.id === selectedSysId);
          if (!foundSys) {
            alert("Sistema de Tubulação inválido ou não encontrado.");
            return;
          }
          resolvedPipingSystemId = foundSys.id;
          resolvedSystem = foundSys.tag;
          resolvedPipingSystemApplicability = 'APPLICABLE';
        }
      } else {
        resolvedPipingSystemId = null;
        resolvedPipingSystemApplicability = undefined;
      }

      const parseMeas = (val: FormDataEntryValue | null, unitVal: FormDataEntryValue | null) => {
        if (!val || String(val).trim() === '') return null;
        const n = Number(val);
        if (isNaN(n)) return null;
        return { value: n, unit: String(unitVal || '') };
      };

      const designPressure = parseMeas(formData.get('designPressure'), formData.get('designPressureUnit'));
      const operatingPressure = parseMeas(formData.get('operatingPressure'), formData.get('operatingPressureUnit'));
      const designTemperature = parseMeas(formData.get('designTemperature'), formData.get('designTemperatureUnit'));
      const operatingTemperature = parseMeas(formData.get('operatingTemperature'), formData.get('operatingTemperatureUnit'));
      const nominalThickness = parseMeas(formData.get('nominalThickness'), 'mm');
      const corrosionAllowance = parseMeas(formData.get('corrosionAllowance'), 'mm');
      const outsideDiameter = parseMeas(formData.get('outsideDiameter'), 'mm'); 
      const pmtaValue = formData.get('pmtaValue') ? parseFloat(formData.get('pmtaValue') as string) : null;
      const pmtaUnit = (formData.get('pmtaUnit') as string) || null;

      let scheduleVal = (formData.get('schedule') as string) || null;
      let pipingDesignationVal = null;
      if (scheduleVal && scheduleVal.startsWith('{')) {
        try {
          const parsed = JSON.parse(scheduleVal);
          scheduleVal = parsed.schedule;
          pipingDesignationVal = parsed.designation;
        } catch {}
      }

      const pipingData = {
        nps: (formData.get('nps') as string) || null,
        schedule: scheduleVal,
        pipingDesignation: pipingDesignationVal,
        outsideDiameter: parseMeas(formData.get('outsideDiameter'), 'mm'),
        insulation: (formData.get('insulation') as string) || null,
        spec: (formData.get('spec') as string) || null,
        pressureClass: (formData.get('pressureClass') as string) || null,
        materialFamily: (formData.get('materialFamily') as string) || null,
        materialSpecification: (formData.get('materialSpecification') as string) || null,
        materialGrade: (formData.get('materialGrade') as string) || null,
        dimensionalStandard: (formData.get('dimensionalStandard') as string) || null,
        calculatedInnerDiameter: parseMeas(formData.get('calculatedInnerDiameter'), 'mm'),
        dimensionSource: (formData.get('dimensionSource') as string) || null,
        dimensionManualSource: (formData.get('dimensionManualSource') as string) || null,
        dimensionManualJustification: (formData.get('dimensionManualJustification') as string) || null,
        lineNumber: (formData.get('lineNumber') as string) || null,
        fromReference: (formData.get('fromReference') as string) || null,
        toReference: (formData.get('toReference') as string) || null
      };

      const payload = {
        design: {
          pressure: designPressure,
          temperature: designTemperature,
          material: eqType === 'PIPING_LINE' ? `${pipingData.materialSpecification || ''} ${pipingData.materialGrade || ''}`.trim() || 'NÃO INFORMADO' : formData.get('material') || null,
          fabricationStandard: formData.get('fabricationStandard') || null,
          projectCode: formData.get('projectCode') || null,
          drawingReference: (formData.get('drawingReference') as string)?.trim() || null,
          flowDiagramReference: (formData.get('flowDiagramReference') as string)?.trim() || null,
          nominalThickness: nominalThickness,
          corrosionAllowance: corrosionAllowance
        },
        operation: {
          pressure: operatingPressure,
          temperature: operatingTemperature,
          fluid: formData.get('fluid') || null,
          fluidClass: formData.get('fluidClass') || null
        },
        inService: {
          inspectionCode: formData.get('inServiceInspectionCode') || null
        },
        pipingLines: []
      };

      const updatedEquipment: Equipment = {
        ...eq,
        tag: (formData.get('tag') as string).trim().toUpperCase(),
        name: formData.get('name') as string,
        equipmentType: eqType,
        projectId: projectId as any,
        unitAreaId: unitAreaId as any,
        areaId: unitAreaId as any,
        system: resolvedSystem,
        pipeSystemApplicability: resolvedPipingSystemApplicability,
        pipingSystemId: resolvedPipingSystemId,
        service: (formData.get('fluid') as string) || (formData.get('name') as string) || null,
        manufacturer: (formData.get('manufacturer') as string) || null,
        serialNumber: (formData.get('serialNumber') as string) || null,
        yearOfManufacture: formData.get('yearOfManufacture') ? Number(formData.get('yearOfManufacture')) : null,
        criticality: (formData.get('criticality') as any) || 'MEDIUM',
        status: (formData.get('status') as any) || 'ACTIVE',
        notes: (formData.get('notes') as string) || null,

        ...(eqType === 'PIPING_LINE' ? {
          nps: pipingData.nps,
          schedule: pipingData.schedule,
          pipingDesignation: pipingData.pipingDesignation,
          outsideDiameter: pipingData.outsideDiameter,
          insulation: pipingData.insulation,
          spec: pipingData.spec,
          pressureClass: pipingData.pressureClass,
          materialFamily: pipingData.materialFamily,
          materialSpecification: pipingData.materialSpecification,
          materialGrade: pipingData.materialGrade,
          dimensionalStandard: pipingData.dimensionalStandard,
          calculatedInnerDiameter: pipingData.calculatedInnerDiameter,
          dimensionSource: pipingData.dimensionSource as any,
          dimensionManualSource: pipingData.dimensionManualSource,
          dimensionManualJustification: pipingData.dimensionManualJustification,
          lineNumber: pipingData.lineNumber,
          fromReference: pipingData.fromReference,
          toReference: pipingData.toReference
        } : {
          lineNumber: null,
          fromReference: null,
          toReference: null,
          nps: null,
          schedule: null,
          pipingDesignation: null,
          outsideDiameter: null,
          insulation: null,
          spec: null,
          pressureClass: null,
          materialFamily: null,
          materialSpecification: null,
          materialGrade: null,
          dimensionalStandard: null,
          calculatedInnerDiameter: null,
          dimensionSource: undefined,
          dimensionManualSource: null,
          dimensionManualJustification: null
        }),

        material: eqType === 'PIPING_LINE' ? `${pipingData.materialSpecification || ''} ${pipingData.materialGrade || ''}`.trim() || 'NÃO INFORMADO' : (formData.get('material') as string) || null,
        fluid: (formData.get('fluid') as string) || null,
        fluidClass: (formData.get('fluidClass') as string) || null,
        designPressure: designPressure,
        operatingPressure: operatingPressure,
        designTemperature: designTemperature,
        pmtaValue: pmtaValue,
        pmtaUnit: pmtaUnit,
        operatingTemperature: operatingTemperature,
        nominalThickness: nominalThickness,
        corrosionAllowance: corrosionAllowance,
        fabricationStandard: (formData.get('fabricationStandard') as string) || null,
        projectCode: (formData.get('projectCode') as string) || null,
        drawingReference: (formData.get('drawingReference') as string)?.trim() || null,
        flowDiagramReference: (formData.get('flowDiagramReference') as string)?.trim() || null,
        inServiceInspectionCode: (formData.get('inServiceInspectionCode') as string) || null,
        nr13Applicability: (formData.get('nr13Applicability') as any) || 'UNCLASSIFIED',
        nr13Category: (formData.get('nr13Category') as string) || null,
        internalCoating: (formData.get('internalCoating') as string) || null,
        externalCoating: (formData.get('externalCoating') as string) || null,
        payloadJson: JSON.stringify(payload),
        updatedAt: new Date().toISOString()
      };

      try {
        const dummyContext = { activeClientId, permissions: contextStore.get().permissions, correlationId: '', userId: contextStore.get().userId } as any;
        await window.app.platform.equipmentService.updateEquipment(dummyContext, updatedEquipment);
        closeModal();
        this.loadData();
      } catch (err: any) {
        alert(`Erro ao atualizar equipamento: ${err.message}`);
      }
    });
  }

  // INACTIVATE MODAL
  private openInactivateModal(id: string, tag: string) {
    const modalContainer = this.querySelector('#eq-modal-container') as HTMLElement;
    if (!modalContainer) return;

    modalContainer.innerHTML = `
      <div class="modal-backdrop" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.8); z-index: 999; display: flex; align-items: center; justify-content: center; padding: 20px;">
        <div style="background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 8px; width: 100%; max-width: 480px; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.5);">
          <div style="padding: 16px 20px; border-bottom: 1px solid #27272a; display: flex; justify-content: space-between; align-items: center;">
            <h3 style="margin: 0; font-size: 1.1rem; color: var(--status-warning-text); display: flex; align-items: center; gap: 8px;">
              <i data-lucide="power" style="width: 20px; height: 20px;"></i> Inativar Equipamento ${tag}
            </h3>
            <button id="btn-close-modal" style="background: transparent; border: none; color: var(--color-text-disabled); font-size: 1.2rem; cursor: pointer;">✕</button>
          </div>
          
          <form id="form-inactivate-eq" style="padding: 20px; display: flex; flex-direction: column; gap: 14px;">
            <p style="margin: 0; font-size: 0.875rem; color: var(--color-text-primary);">
              A inativação preservará todo o histórico técnico, inspeções e documentos, alterando o status do ativo para <strong>INATIVO</strong>.
            </p>

            <div>
              <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Motivo / Justificativa da Inativação *</label>
              <textarea name="reason" required minlength="3" rows="3" placeholder="Ex: Equipamento desativado na reforma da unidade 2026..." style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary); resize: vertical;"></textarea>
            </div>

            <div style="display: flex; justify-content: flex-end; gap: 8px; border-top: 1px solid #27272a; padding-top: 16px;">
              <button type="button" id="btn-cancel-modal" class="btn-secondary-eq">Cancelar</button>
              <button type="submit" style="background: var(--status-warning-text); color: var(--color-text-inverse); border: none; padding: 8px 16px; border-radius: 6px; font-weight: 600; cursor: pointer;">Confirmar Inativação</button>
            </div>
          </form>
        </div>
      </div>
    `;

    createIcons({
      icons: { Power },
      nameAttr: "data-lucide",
      root: modalContainer
    });

    const closeModal = () => { modalContainer.innerHTML = ''; };
    modalContainer.querySelector('#btn-close-modal')?.addEventListener('click', closeModal);
    modalContainer.querySelector('#btn-cancel-modal')?.addEventListener('click', closeModal);

    const form = modalContainer.querySelector('#form-inactivate-eq') as HTMLFormElement;
    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const reason = (form.querySelector('textarea[name="reason"]') as HTMLTextAreaElement).value;
      const targetEq = this.equipments.find(e => e.id === id);
      const activeClientId = targetEq?.clientId || contextStore.get().activeClientId;

      try {
        const dummyContext = { activeClientId, permissions: contextStore.get().permissions, correlationId: '', userId: contextStore.get().userId } as any;
        await window.app.platform.equipmentService.inactivateEquipment(dummyContext, id, reason);
        closeModal();
        this.loadData();
      } catch (err: any) {
        alert(`Erro ao inativar equipamento: ${err.message}`);
      }
    });
  }

  // DELETE MODAL (CONTROLLED HARD DELETE)
  private async openDeleteModal(id: string, tag: string) {
    const modalContainer = this.querySelector('#eq-modal-container') as HTMLElement;
    if (!modalContainer) return;

    const targetEq = this.equipments.find(e => e.id === id);
    const activeClientId = targetEq?.clientId || contextStore.get().activeClientId;
    const dummyContext = { activeClientId, permissions: contextStore.get().permissions, correlationId: '', userId: contextStore.get().userId } as any;

    let eligibility: any;
    try {
      eligibility = await window.app.platform.equipmentService.checkDeleteEligibility(dummyContext, id);
    } catch (err: any) {
      alert(err.safeMessage || err.message);
      return;
    }

    // Modal para bloqueio absoluto (RI existente)
    if (!eligibility.canDelete || eligibility.hasReport) {
      modalContainer.innerHTML = `
        <div class="modal-backdrop" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.8); z-index: 999; display: flex; align-items: center; justify-content: center; padding: 20px;">
          <div style="background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 8px; width: 100%; max-width: 500px; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.5);">
            <div style="padding: 16px 20px; border-bottom: 1px solid #27272a; display: flex; justify-content: space-between; align-items: center;">
              <h3 style="margin: 0; font-size: 1.1rem; color: var(--status-warning-solid); display: flex; align-items: center; gap: 8px;">
                <i data-lucide="alert-triangle" style="width: 20px; height: 20px;"></i> NÃO É POSSÍVEL EXCLUIR ESTE EQUIPAMENTO
              </h3>
              <button id="btn-close-modal" style="background: transparent; border: none; color: var(--color-text-disabled); font-size: 1.2rem; cursor: pointer;">✕</button>
            </div>
            
            <div style="padding: 20px; color: var(--color-text-primary); font-size: 0.95rem; line-height: 1.5;">
              <p style="margin-top: 0;">${eligibility.blockReason || 'Este equipamento possui Relatório de Integridade vinculado e faz parte do histórico documental do INSPECT IA.'}</p>
              <p style="margin-bottom: 0;">Utilize a opção de inativação/arquivamento, quando disponível.</p>
            </div>

            <div style="display: flex; justify-content: flex-end; gap: 8px; border-top: 1px solid #27272a; padding: 16px 20px;">
              <button type="button" id="btn-ok-modal" style="background: var(--color-primary); color: var(--color-text-primary); border: none; padding: 8px 16px; border-radius: 6px; font-weight: 600; cursor: pointer;">OK</button>
            </div>
          </div>
        </div>
      `;

      createIcons({ icons: { AlertTriangle }, nameAttr: "data-lucide", root: modalContainer });
      const closeModal = () => { modalContainer.innerHTML = ''; };
      modalContainer.querySelector('#btn-close-modal')?.addEventListener('click', closeModal);
      modalContainer.querySelector('#btn-ok-modal')?.addEventListener('click', closeModal);
      return;
    }

    // Modal para confirmação de exclusão (com ou sem dependências em cascata)
    modalContainer.innerHTML = `
      <div class="modal-backdrop" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.8); z-index: 999; display: flex; align-items: center; justify-content: center; padding: 20px;">
        <div style="background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 8px; width: 100%; max-width: 500px; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.5);">
          <div style="padding: 16px 20px; border-bottom: 1px solid #27272a; display: flex; justify-content: space-between; align-items: center;">
            <h3 style="margin: 0; font-size: 1.1rem; color: var(--status-danger-solid); display: flex; align-items: center; gap: 8px;">
              <i data-lucide="trash-2" style="width: 20px; height: 20px;"></i> EXCLUIR EQUIPAMENTO?
            </h3>
            <button id="btn-close-modal" style="background: transparent; border: none; color: var(--color-text-disabled); font-size: 1.2rem; cursor: pointer;">✕</button>
          </div>
          
          <form id="form-delete-eq" style="padding: 20px; display: flex; flex-direction: column; gap: 14px;">
            <div style="color: var(--color-text-primary); font-size: 0.95rem;">
              <p style="margin-top: 0;">Você está prestes a excluir:</p>
              <ul style="list-style: none; padding: 0; margin-bottom: 10px; background: var(--color-input-bg); padding: 12px; border-radius: 6px;">
                <li><strong>TAG:</strong> ${tag}</li>
                ${targetEq?.name ? `<li><strong>DESCRIÇÃO:</strong> ${targetEq.name}</li>` : ''}
                <li><strong>TIPO:</strong> ${targetEq?.equipmentType || '-'}</li>
              </ul>
              <p style="margin-bottom: 0; color: var(--status-danger-solid); font-weight: bold;">Esta ação não poderá ser desfeita.</p>
            </div>

            ${eligibility.hasDependencies ? `
            <div style="background: rgba(239, 68, 68, 0.1); border: 1px solid rgba(239, 68, 68, 0.3); border-radius: 6px; padding: 12px; color: var(--status-danger-border); font-size: 0.85rem;">
              <strong>Atenção:</strong> Este equipamento possui dados de inspeção em andamento, recomendações ou anexos. Confirme o cancelamento/exclusão desses registros antes de excluir o equipamento.
            </div>
            ` : ''}

            <div>
              <label style="display: block; font-size: 0.8rem; color: var(--color-text-disabled); margin-bottom: 4px;">Motivo / Justificativa da Exclusão *</label>
              <textarea name="reason" required minlength="3" rows="3" placeholder="Ex: Cadastro duplicado por engano..." style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary); resize: vertical;"></textarea>
            </div>

            <div style="display: flex; justify-content: flex-end; gap: 8px; border-top: 1px solid #27272a; padding-top: 16px;">
              <button type="button" id="btn-cancel-modal" class="btn-secondary-eq">Cancelar</button>
              <button type="submit" style="background: var(--status-danger-solid); color: var(--color-text-primary); border: none; padding: 8px 16px; border-radius: 6px; font-weight: 600; cursor: pointer;">Excluir Equipamento</button>
            </div>
          </form>
        </div>
      </div>
    `;

    createIcons({ icons: { Trash2 }, nameAttr: "data-lucide", root: modalContainer });

    const closeModal = () => { modalContainer.innerHTML = ''; };
    modalContainer.querySelector('#btn-close-modal')?.addEventListener('click', closeModal);
    modalContainer.querySelector('#btn-cancel-modal')?.addEventListener('click', closeModal);

    const form = modalContainer.querySelector('#form-delete-eq') as HTMLFormElement;
    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const reason = (form.querySelector('textarea[name="reason"]') as HTMLTextAreaElement).value;

      try {
        const res = await window.app.platform.equipmentService.deleteEquipment(dummyContext, id, reason);
        closeModal();
        this.loadData();
      } catch (err: any) {
        alert(err.safeMessage || err.message);
      }
    });
  }
}

if (!customElements.get("app-equipment-list-page")) {
  customElements.define("app-equipment-list-page", AppEquipmentListPage);
}




